import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import ClassworkCompetencySelector from '../components/classwork/ClassworkCompetencySelector';
import ClassworkSubmissionBoard from '../components/classwork/ClassworkSubmissionBoard';
import ClassworkCorrectionReview from '../components/classwork/ClassworkCorrectionReview';
import ClassworkLlmCorrectionView from '../components/classwork/ClassworkLlmCorrectionView';
import ClassworkRubricAcknowledgePanel from '../components/classwork/ClassworkRubricAcknowledgePanel';
import ClassworkPenPaperImport from '../components/classwork/ClassworkPenPaperImport';
import ClassworkResultsExportModal from '../components/classwork/ClassworkResultsExportModal';
import OnlyOfficeSessionModal from '../components/classwork/OnlyOfficeSessionModal';
import ClassworkPipelineReviewModal from '../components/classwork/ClassworkPipelineReviewModal';
import {
  fetchClassworkList,
  createClasswork,
  updateClasswork,
  deleteClasswork,
  duplicateClasswork,
  fetchClassworkStudentDirectory,
  fetchClassworkDetail,
  requestCompetencySuggestions,
  confirmCompetencies,
  queueCorrection,
  rerunCorrection,
  restoreArchivedCorrection,
  buildReferenceVersion,
  cancelReferenceVersionBuild,
  listReferenceVersions,
  activateReferenceVersion,
  finalizeGrade,
  publishGrade,
  archiveSubmissionForResubmission,
  restoreArchivedSubmission,
  generateAssignments,
  resolveClassworkAssignmentTargets,
  deleteClassworkMaterial,
  openClassworkMaterialOnlyofficeSession,
  fetchJobStatus,
  importClassworkTeacherUploadRows,
  downloadClassworkPrintSheets,
  startClassworkTeacherBatchImport,
  updateClassworkTeacherBatchImportSegments,
  finalizeClassworkTeacherBatchImport,
  resetOnlyofficeRetry,
  uploadClassworkMaterial,
  createClassworkMaterialLink,
  uploadClassworkTeacherScan,
  uploadClassworkTeacherScansBulk,
  fetchClassworkAnalysisDiagnostics,
  fetchClassworkPipelineDecisions,
  fetchClassworkPipelineMetrics,
  fetchClassworkPipelineReviewReasons,
  fetchClassworkCorrections,
  fetchGradeSchemas,
  fetchDefaultGradeSchemaForSubject,
  ClassworkReferenceVersion,
  type ClassworkAssignment,
  type TeacherUploadBatchStrategy
} from '../lib/classworkApi';
import { fetchTeachingMeta, fetchTeachingUnitDetail } from '../lib/unterrichtenApi';
import {
  Archive,
  Copy,
  Eye,
  EyeOff,
  FileText,
  Trash2,
  ChevronRight,
  LayoutGrid,
  PlusCircle,
  Upload,
  Pencil,
  Printer
} from 'lucide-react';

function classworkStatusBadge(status?: string) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return { label: 'Aktiv', className: 'bg-emerald-50 text-emerald-800 border border-emerald-200' };
  if (s === 'closed') return { label: 'Ausgeblendet', className: 'bg-amber-50 text-amber-800 border border-amber-200' };
  if (s === 'archived') return { label: 'Archiv', className: 'bg-slate-100 text-slate-700 border border-slate-200' };
  if (s === 'draft') return { label: 'Entwurf', className: 'bg-gray-100 text-gray-700 border border-gray-200' };
  return { label: status || '—', className: 'bg-gray-100 text-gray-700 border border-gray-200' };
}

function entryTypeShort(entry?: string) {
  const e = String(entry || '');
  if (e === 'classwork_digital') return 'Digital';
  if (e === 'classwork_penpaper' || e === 'classwork_offline') return 'Papier/Offline';
  return e.replace(/_/g, ' ') || '—';
}

function isOfficeMaterial(mimeType?: string, originalName?: string) {
  const mime = String(mimeType || '').toLowerCase();
  const name = String(originalName || '').toLowerCase();
  return (
    mime.includes('officedocument') ||
    mime.includes('msword') ||
    mime.includes('ms-excel') ||
    mime.includes('ms-powerpoint') ||
    name.endsWith('.doc') ||
    name.endsWith('.docx') ||
    name.endsWith('.xls') ||
    name.endsWith('.xlsx') ||
    name.endsWith('.ppt') ||
    name.endsWith('.pptx') ||
    name.endsWith('.txt')
  );
}

function materialTypeLabel(materialType?: string) {
  if (materialType === 'aufgabenstellung') return 'Aufgabenstellung';
  if (materialType === 'rueckgabe') return 'Rückgabedokument';
  if (materialType === 'erwartungshorizont') return 'Korrekturraster (nur Lehrkraft)';
  if (materialType === 'musterloesung') return 'Musterlösung (nur Lehrkraft)';
  return 'Zusätzliche Datei';
}

function referenceVersionStatusLabel(status?: string) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return { label: 'Aktiv', className: 'bg-emerald-50 text-emerald-800 border border-emerald-200' };
  if (s === 'ready') return { label: 'Bereit', className: 'bg-blue-50 text-blue-800 border border-blue-200' };
  if (s === 'building') return { label: 'Building', className: 'bg-amber-50 text-amber-800 border border-amber-200' };
  if (s === 'failed') return { label: 'Fehlgeschlagen', className: 'bg-red-50 text-red-800 border border-red-200' };
  return { label: status || '—', className: 'bg-gray-100 text-gray-700 border border-gray-200' };
}

function materialRowType(m: any) {
  return String(m?.material_type ?? m?.materialType ?? '');
}

function isExpectationHorizonMaterial(m: any) {
  return materialRowType(m) === 'erwartungshorizont';
}

function isMusterloesungMaterial(m: any) {
  return materialRowType(m) === 'musterloesung';
}

function toDatetimeLocalValue(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localDatetimeToIso(local: string) {
  if (!local?.trim()) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

type ReturnModeChoice = 'digital_system' | 'paper_presence' | 'paper_photo_upload' | 'results_import_only';
type DigitalSubmissionChoice = 'onlyoffice' | 'tiptap' | 'upload';
type StudentUploadMode = 'all_common' | 'images_pdf' | 'images_only' | 'pdf_only';

function resolveReturnMode(item: any): ReturnModeChoice {
  const entryType = String(item?.entry_type || '').toLowerCase();
  const hasOffline = Boolean(Number(item?.offline_workflow_enabled ?? item?.penpaper_enabled ?? 0));
  const hasOnlyoffice = Boolean(Number(item?.onlyoffice_enabled ?? 0));
  const hasTiptap = Boolean(Number(item?.tiptap_enabled ?? 0));
  const hasUpload = Boolean(Number(item?.student_file_upload_enabled ?? 0));
  if (hasOffline && entryType === 'classwork_offline') return 'paper_photo_upload';
  if (hasOffline) return 'paper_presence';
  if (!hasOnlyoffice && !hasTiptap && !hasUpload) return 'results_import_only';
  return 'digital_system';
}

function returnModeLabel(mode: ReturnModeChoice) {
  if (mode === 'digital_system') return 'Digital im System';
  if (mode === 'paper_presence') return 'Papier in Präsenz';
  if (mode === 'paper_photo_upload') return 'Abfotografieren';
  return 'Nur Ergebnisse importieren';
}

function classworkProgressLabel(item: any) {
  const status = String(item?.status || '').toLowerCase();
  const mode = resolveReturnMode(item);
  if (status === 'draft') return 'Konzeption läuft';
  if (status === 'archived') return 'Abgeschlossen';
  if (status === 'closed') return 'Für Schüler ausgeblendet';
  if (mode === 'paper_presence') return 'Rückläufe einsammeln';
  if (mode === 'results_import_only') return 'Ergebnisse importieren';
  return 'Abgaben prüfen und bewerten';
}

const RECORRECTION_RELEVANT_STATES = new Set(['reviewed', 'graded', 'published']);

function normalizeComparableValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? '1' : '0';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  return String(value).trim();
}

function hasPayloadChangesAgainstClasswork(payload: Record<string, unknown>, currentItem: any): boolean {
  if (!currentItem) return false;
  return Object.keys(payload).some((key) => {
    const nextValue = normalizeComparableValue(payload[key]);
    const currentValue = normalizeComparableValue(currentItem?.[key]);
    return nextValue !== currentValue;
  });
}

function estimateRecorrectionCandidates(assignments: any[], submissions: any[]): number {
  const reviewedOrGradedAssignmentIds = new Set<number>();
  for (const assignment of assignments || []) {
    const assignmentId = Number((assignment as any)?.id ?? (assignment as any)?.assignment_id);
    if (!Number.isFinite(assignmentId) || assignmentId <= 0) continue;
    const state = String((assignment as any)?.state || '').trim().toLowerCase();
    if (RECORRECTION_RELEVANT_STATES.has(state)) {
      reviewedOrGradedAssignmentIds.add(assignmentId);
    }
  }
  if (reviewedOrGradedAssignmentIds.size > 0) return reviewedOrGradedAssignmentIds.size;
  const submissionAssignmentIds = new Set<number>();
  for (const submission of submissions || []) {
    const assignmentId = Number((submission as any)?.assignment_id);
    if (!Number.isFinite(assignmentId) || assignmentId <= 0) continue;
    submissionAssignmentIds.add(assignmentId);
  }
  return submissionAssignmentIds.size;
}

/** Klassenarbeit-Wizard: einheitliche Fußzeilen & Buttons (immer `btn` + Größe + Variante) */
const WIZARD_ACTION_ROW = 'flex flex-wrap items-center gap-3 pt-6 mt-2 border-t border-gray-200';
const WIZARD_BTN_PRIMARY = 'btn btn-md btn-primary shadow-sm font-semibold';
const WIZARD_BTN_DRAFT =
  'btn btn-md btn-outline font-medium border-gray-300 bg-white text-gray-800 hover:bg-gray-50';
const WIZARD_BTN_BACK = 'btn btn-md btn-secondary font-medium';
const WIZARD_BTN_PRIMARY_LG = 'btn btn-lg btn-primary shadow-md font-semibold';

export default function ClassworkPage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const teachingUnitIdRaw = searchParams.get('teachingUnitId');
  const teachingUnitIdFromUrl = teachingUnitIdRaw ? Number(teachingUnitIdRaw) : NaN;
  const linkedTeachingUnitId =
    Number.isFinite(teachingUnitIdFromUrl) && teachingUnitIdFromUrl > 0 ? teachingUnitIdFromUrl : null;

  const teachingUnitQuery = useQuery(
    ['unterrichten', 'detail', 'classwork-context', linkedTeachingUnitId],
    () => fetchTeachingUnitDetail(linkedTeachingUnitId as number),
    { enabled: !!linkedTeachingUnitId }
  );

  const [mainTab, setMainTab] = useState<'list' | 'create'>('list');
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [wizardClassworkId, setWizardClassworkId] = useState<number | null>(null);
  const [listDetailId, setListDetailId] = useState<number | null>(null);

  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [pipelineReviewAssignmentId, setPipelineReviewAssignmentId] = useState<number | null>(null);
  const [runningCorrectionsByAssignmentId, setRunningCorrectionsByAssignmentId] = useState<Record<number, boolean>>({});
  /** Live Kurztext aus job_update.progressMessage (dieser Browser) — fallback: latest_corrections.pipeline_status */
  const [correctionPipelineLabelByAssignmentId, setCorrectionPipelineLabelByAssignmentId] = useState<Record<number, string>>(
    {}
  );
  const [isBatchCorrectionRunning, setIsBatchCorrectionRunning] = useState(false);
  const [isBatchPublishRunning, setIsBatchPublishRunning] = useState(false);
  const [autoAnalyzeNewSubmissions, setAutoAnalyzeNewSubmissions] = useState(false);
  const [isAutoAnalyzeTickRunning, setIsAutoAnalyzeTickRunning] = useState(false);
  const autoAnalyzeLastSubmissionByAssignmentRef = useRef<Record<number, number>>({});
  const [isBuildingReferenceVersion, setIsBuildingReferenceVersion] = useState(false);
  const [isCancellingReferenceVersionBuild, setIsCancellingReferenceVersionBuild] = useState(false);
  const [isActivatingReferenceVersion, setIsActivatingReferenceVersion] = useState<number | null>(null);
  const [showResultsExport, setShowResultsExport] = useState(false);

  const [wTitle, setWTitle] = useState('');
  const [wSubjectId, setWSubjectId] = useState('');
  const [wCurriculumId, setWCurriculumId] = useState('');
  const [wDescription, setWDescription] = useState('');
  const [wEstimatedMinutes, setWEstimatedMinutes] = useState('');
  const [wStartsAt, setWStartsAt] = useState('');
  const [wDueAt, setWDueAt] = useState('');
  const [wLatestSubmitAt, setWLatestSubmitAt] = useState('');
  const [wMaxAttempts, setWMaxAttempts] = useState('1');
  const [wSebMode, setWSebMode] = useState<'seb_optional' | 'seb_required'>('seb_optional');
  const [wExpectation, setWExpectation] = useState('');
  const [wSample, setWSample] = useState('');
  const [wGradeSchemaId, setWGradeSchemaId] = useState('');
  const [wStudentUploadMode, setWStudentUploadMode] = useState<StudentUploadMode>('all_common');

  const [materialType, setMaterialType] = useState<'aufgabenstellung' | 'rueckgabe' | 'zusaetzlich'>('aufgabenstellung');
  const [materialFile, setMaterialFile] = useState<File | null>(null);
  const [materialTitle, setMaterialTitle] = useState<string>('');
  const [materialLinkUrl, setMaterialLinkUrl] = useState<string>('');
  const [materialLinkTitle, setMaterialLinkTitle] = useState<string>('');
  const [materialRequired, setMaterialRequired] = useState<boolean>(false);
  const [materialSession, setMaterialSession] = useState<any | null>(null);
  const [materialEditorTargetId, setMaterialEditorTargetId] = useState<number | null>(null);
  const [showWizardAdvancedSchedule, setShowWizardAdvancedSchedule] = useState(false);

  const [assignmentScope, setAssignmentScope] = useState<'class' | 'course' | 'group' | 'manual'>('class');
  const [assignmentClassName, setAssignmentClassName] = useState<string>('');
  const [assignmentCourseId, setAssignmentCourseId] = useState<string>('');
  const [assignmentGroupId, setAssignmentGroupId] = useState<string>('');
  const [manualQuery, setManualQuery] = useState<string>('');
  const [manualSelectedById, setManualSelectedById] = useState<Record<string, boolean>>({});

  const listQuery = useQuery(['classwork-list'], () => fetchClassworkList());
  const referenceVersionsQuery = useQuery(
    ['classwork-reference-versions', listDetailId],
    () => listReferenceVersions(Number(listDetailId)),
    { enabled: !!listDetailId }
  );
  const teachingMetaQuery = useQuery(['unterrichten-meta', 'classwork'], () => fetchTeachingMeta());
  const studentDirectoryQuery = useQuery(['classwork-student-directory'], () => fetchClassworkStudentDirectory());

  const wizardDetailQuery = useQuery(['classwork-detail', wizardClassworkId], () => fetchClassworkDetail(Number(wizardClassworkId)), {
    enabled: !!wizardClassworkId
  });
  const wizardGradeSchemasQuery = useQuery(['classwork-grade-schemas-wizard'], () => fetchGradeSchemas(), {
    enabled: Boolean(wizardClassworkId) && wizardStep >= 3
  });
  const listDetailQuery = useQuery(['classwork-detail', listDetailId], () => fetchClassworkDetail(Number(listDetailId)), {
    enabled: !!listDetailId,
    refetchInterval: (data: any) => {
      const submissions = data?.submissions || [];
      const hasPreparationRunning = submissions.some((s: any) => {
        const analysisStatus = String(s?.analysis_status || '').toLowerCase();
        const ocrStatus = String(s?.ocr_status || '').toLowerCase();
        return ['pending', 'running'].includes(analysisStatus) || ['pending', 'processing'].includes(ocrStatus);
      });
      const hasCorrectionRunning = Object.keys(runningCorrectionsByAssignmentId).length > 0;
      const corrections = data?.latest_corrections || [];
      const hasServerCorrectionRunning = corrections.some((c: any) => {
        const st = String(c?.status || '').toLowerCase();
        return st === 'queued' || st === 'processing';
      });
      return hasPreparationRunning || hasCorrectionRunning || hasServerCorrectionRunning ? 2000 : false;
    }
  });

  useEffect(() => {
    const subject = wizardDetailQuery.data?.item?.subject_id;
    const curriculum = wizardDetailQuery.data?.item?.curriculum_id;
    setWSubjectId(subject ? String(subject) : '');
    setWCurriculumId(curriculum ? String(curriculum) : '');
  }, [wizardDetailQuery.data?.item?.id, wizardDetailQuery.data?.item?.subject_id, wizardDetailQuery.data?.item?.curriculum_id]);

  useEffect(() => {
    const it = wizardDetailQuery.data?.item;
    if (!it || !wizardClassworkId) return;
    setWTitle(it.title || '');
    setWDescription(it.description || '');
    setWEstimatedMinutes(it.estimated_duration_minutes != null ? String(it.estimated_duration_minutes) : '');
    setWStartsAt(toDatetimeLocalValue(it.starts_at));
    setWDueAt(toDatetimeLocalValue(it.due_at));
    setWLatestSubmitAt(toDatetimeLocalValue(it.latest_submit_at));
    setWMaxAttempts(String(it.max_attempts ?? 1));
    setWSebMode(it.seb_mode === 'seb_required' ? 'seb_required' : 'seb_optional');
    setWStudentUploadMode(
      String(it.student_upload_mode || '').trim().toLowerCase() === 'images_pdf'
        ? 'images_pdf'
        : String(it.student_upload_mode || '').trim().toLowerCase() === 'images_only'
          ? 'images_only'
          : String(it.student_upload_mode || '').trim().toLowerCase() === 'pdf_only'
            ? 'pdf_only'
            : 'all_common'
    );
    setWExpectation(it.expectation_horizon || '');
    setWSample(it.sample_solution || '');
    const gsid = it.grade_schema_id;
    setWGradeSchemaId(gsid != null && Number(gsid) > 0 ? String(gsid) : '');
  }, [wizardClassworkId, wizardDetailQuery.data?.item?.id, wizardDetailQuery.data?.item?.updated_at]);

  const resetWizard = () => {
    setWizardStep(1);
    setWizardClassworkId(null);
    setSuggestions([]);
    setWTitle('');
    setWSubjectId('');
    setWCurriculumId('');
    setWDescription('');
    setWEstimatedMinutes('');
    setWStartsAt('');
    setWDueAt('');
    setWLatestSubmitAt('');
    setWMaxAttempts('1');
    setWSebMode('seb_optional');
    setWStudentUploadMode('all_common');
    setWExpectation('');
    setWSample('');
    setWGradeSchemaId('');
    setAssignmentScope('class');
    setAssignmentClassName('');
    setAssignmentCourseId('');
    setAssignmentGroupId('');
    setManualSelectedById({});
    setManualQuery('');
  };

  const duplicateMutation = useMutation((id: number) => duplicateClasswork(id), {
    onSuccess: () => {
      toast.success('Duplikat angelegt');
      queryClient.invalidateQueries(['classwork-list']);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Duplizieren fehlgeschlagen');
    }
  });

  const openClassworkForEdit = useCallback(
    (id: number) => {
      setWizardClassworkId(id);
      setWizardStep(1);
      setMainTab('create');
      setSuggestions([]);
      void queryClient.invalidateQueries(['classwork-detail', id]);
    },
    [queryClient]
  );

  const patchWizard = useCallback(
    async (payload: Record<string, unknown>) => {
      if (!wizardClassworkId) return false;
      const existingItem = wizardDetailQuery.data?.item;
      if (hasPayloadChangesAgainstClasswork(payload, existingItem)) {
        const recorrectionCount = estimateRecorrectionCandidates(
          wizardDetailQuery.data?.assignments || [],
          wizardDetailQuery.data?.submissions || []
        );
        if (recorrectionCount > 0) {
          const ok = window.confirm(
            `Die Klassenarbeit wurde verändert. ${recorrectionCount} bereits bearbeitete Arbeit(en) müssten ggf. neu korrigiert werden.\n\nMöchtest du trotzdem speichern?`
          );
          if (!ok) return false;
        }
      }
      await updateClasswork(wizardClassworkId, payload as any);
      await queryClient.invalidateQueries(['classwork-detail', wizardClassworkId]);
      return true;
    },
    [wizardClassworkId, queryClient, wizardDetailQuery.data?.item, wizardDetailQuery.data?.assignments, wizardDetailQuery.data?.submissions]
  );

  const [digitalSubmissionChoice, setDigitalSubmissionChoice] = useState<DigitalSubmissionChoice>('onlyoffice');
  const [showBackgroundJobs, setShowBackgroundJobs] = useState(false);

  const updateReturnModeMutation = useMutation(
    ({ mode, digitalMode }: { mode: ReturnModeChoice; digitalMode: DigitalSubmissionChoice }) => {
      const payload: any = {
        onlyoffice_enabled: 0,
        tiptap_enabled: 0,
        student_file_upload_enabled: 0,
        penpaper_enabled: 0,
        offline_workflow_enabled: 0
      };
      if (mode === 'digital_system') {
        payload.entry_type = 'classwork_digital';
        payload.onlyoffice_enabled = digitalMode === 'onlyoffice' ? 1 : 0;
        payload.tiptap_enabled = digitalMode === 'tiptap' ? 1 : 0;
        payload.student_file_upload_enabled = digitalMode === 'upload' ? 1 : 0;
        if (digitalMode === 'upload') payload.student_upload_mode = wStudentUploadMode;
      } else if (mode === 'paper_presence') {
        payload.entry_type = 'classwork_penpaper';
        payload.penpaper_enabled = 1;
        payload.offline_workflow_enabled = 1;
      } else if (mode === 'paper_photo_upload') {
        payload.entry_type = 'classwork_offline';
        payload.penpaper_enabled = 1;
        payload.offline_workflow_enabled = 1;
      } else {
        payload.entry_type = 'classwork_offline';
      }
      return updateClasswork(Number(wizardClassworkId), payload);
    },
    {
      onSuccess: () => {
        toast.success('Rücklaufmodus gespeichert');
        void queryClient.invalidateQueries(['classwork-detail', wizardClassworkId]);
        void queryClient.invalidateQueries(['classwork-list']);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Rücklaufmodus konnte nicht gespeichert werden');
      }
    }
  );

  const runAssignmentForClasswork = async (cwId: number) => {
    const payload =
      assignmentScope === 'class'
        ? { scope: 'class' as const, class_name: assignmentClassName }
        : assignmentScope === 'course'
          ? { scope: 'course' as const, course_id: Number(assignmentCourseId) || undefined }
          : assignmentScope === 'group'
            ? { scope: 'group' as const, group_id: Number(assignmentGroupId) || undefined }
            : {
                scope: 'manual' as const,
                student_ids: Object.entries(manualSelectedById)
                  .filter(([, checked]) => !!checked)
                  .map(([id]) => id)
              };
    const resolved = await resolveClassworkAssignmentTargets(cwId, payload as any);
    const ids = Array.isArray(resolved?.student_ids) ? resolved.student_ids : [];
    if (!ids.length) throw new Error('no_students');
    await generateAssignments(cwId, ids);
  };

  const assignFromUnitMutation = useMutation(
    async () => {
      const cwId = wizardClassworkId;
      const uid = linkedTeachingUnitId;
      if (!cwId || !uid) throw new Error('missing_context');
      const detail =
        teachingUnitQuery.data ||
        (await queryClient.fetchQuery(['unterrichten', 'detail', 'classwork-context', uid], () => fetchTeachingUnitDetail(uid)));
      const ids = (detail?.participants || [])
        .map((p) => String(p.student_id || '').trim())
        .filter(Boolean);
      if (!ids.length) throw new Error('no_participants');
      await generateAssignments(cwId, ids);
    },
    {
      onSuccess: () => {
        toast.success('Teilnehmer der Unterrichtseinheit zugewiesen');
        if (wizardClassworkId) void queryClient.invalidateQueries(['classwork-detail', wizardClassworkId]);
      },
      onError: (err: any) => {
        if (err?.message === 'missing_context') {
          toast.error('Zuerst Klassenarbeit anlegen (Schritt 1: Weiter) und Unterrichtseinheit verknüpfen');
          return;
        }
        if (err?.message === 'no_participants') {
          toast.error('Keine Teilnehmer in dieser Unterrichtseinheit');
          return;
        }
        toast.error('Zuweisen fehlgeschlagen');
      }
    }
  );

  const assignByScopeMutation = useMutation(
    async (cwId: number) => {
      await runAssignmentForClasswork(cwId);
    },
    {
      onSuccess: (__, cwId) => {
        toast.success('Zuweisungen gespeichert');
        void queryClient.invalidateQueries(['classwork-detail', cwId]);
      },
      onError: (error: any) => {
        if (error?.message === 'no_students') {
          toast.error('Keine passenden Schüler gefunden');
          return;
        }
        toast.error(error?.response?.data?.error || 'Zuweisung fehlgeschlagen');
      }
    }
  );

  const suggestMutation = useMutation(
    () =>
      requestCompetencySuggestions(Number(wizardClassworkId), {
        source: {
          title: wizardDetailQuery.data?.item?.title || wTitle || '',
          taskText: wizardDetailQuery.data?.item?.description || wDescription || '',
          subjectId: wSubjectId ? Number(wSubjectId) : wizardDetailQuery.data?.item?.subject_id || null,
          curriculumId: wCurriculumId ? Number(wCurriculumId) : wizardDetailQuery.data?.item?.curriculum_id || null
        },
        maxSuggestions: 12
      }),
    {
      onSuccess: (data) => {
        setSuggestions(Array.isArray(data?.items) ? data.items : []);
        toast.success('Kompetenzvorschläge geladen');
      },
      onError: () => {
        toast.error('Vorschläge konnten nicht geladen werden');
      }
    }
  );

  const confirmMutation = useMutation((payload: any) => {
      if (!wizardClassworkId) return Promise.reject(new Error('no_classwork'));
      return confirmCompetencies(wizardClassworkId, payload);
    }, {
    onSuccess: () => {
      toast.success('Kompetenzen gespeichert');
      queryClient.invalidateQueries(['classwork-detail', wizardClassworkId]);
    },
    onError: () => {
      toast.error('Speichern fehlgeschlagen');
    }
  });

  const penpaperMutation = useMutation(
    (payload: {
      classworkId: number;
      rows: Record<string, unknown>[];
      mode: 'skip_duplicates' | 'update_existing' | 'fail_on_conflict';
    }) => importClassworkTeacherUploadRows(Number(payload.classworkId), { rows: payload.rows, mode: payload.mode }),
    {
      onSuccess: (data: any) => {
        toast.success(`Import: ${data?.accepted ?? 0} neu, ${data?.updated ?? 0} aktualisiert`);
        if (listDetailId) queryClient.invalidateQueries(['classwork-detail', listDetailId]);
        if (wizardClassworkId) queryClient.invalidateQueries(['classwork-detail', wizardClassworkId]);
      },
      onError: () => {
        toast.error('Lehrer-Import fehlgeschlagen');
      }
    }
  );

  const penpaperBulkStartMutation = useMutation(
    (payload: { classworkId: number; strategy: TeacherUploadBatchStrategy; file: File }) =>
      startClassworkTeacherBatchImport(Number(payload.classworkId), payload.strategy, payload.file),
    {
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Bulk-Import konnte nicht gestartet werden');
      }
    }
  );

  const penpaperBulkSegmentsMutation = useMutation(
    (payload: {
      classworkId: number;
      runId: number;
      segments: Array<{ segment_no: number; final_student_id?: string | null; page_from: number; page_to: number }>;
    }) => updateClassworkTeacherBatchImportSegments(Number(payload.classworkId), payload.runId, payload.segments),
    {
      onSuccess: () => {
        toast.success('Segmente gespeichert');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Segmente konnten nicht gespeichert werden');
      }
    }
  );

  const penpaperBulkFinalizeMutation = useMutation(
    (payload: { classworkId: number; runId: number }) => finalizeClassworkTeacherBatchImport(Number(payload.classworkId), payload.runId),
    {
      onSuccess: () => {
        toast.success('Bulk-Import finalisiert');
        if (listDetailId) queryClient.invalidateQueries(['classwork-detail', listDetailId]);
        if (wizardClassworkId) queryClient.invalidateQueries(['classwork-detail', wizardClassworkId]);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Bulk-Import Finalisierung fehlgeschlagen');
      }
    }
  );

  const deleteMaterialMutation = useMutation(
    (args: { classworkId: number; materialId: number }) =>
      deleteClassworkMaterial(args.classworkId, args.materialId),
    {
      onSuccess: (_data, variables) => {
        toast.success('Material entfernt');
        void queryClient.invalidateQueries(['classwork-detail', variables.classworkId]);
      },
      onError: () => {
        toast.error('Material konnte nicht gelöscht werden');
      }
    }
  );

  const uploadTeacherContextFileMutation = useMutation(
    async (payload: {
      file: File;
      materialType: 'erwartungshorizont' | 'musterloesung';
      classworkId: number;
    }) => {
      await uploadClassworkMaterial(payload.classworkId, {
        file: payload.file,
        materialType: payload.materialType,
        title: payload.file.name
      });
    },
    {
      onSuccess: (_data, variables) => {
        toast.success('Datei hochgeladen');
        void queryClient.invalidateQueries(['classwork-detail', variables.classworkId]);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Upload fehlgeschlagen');
      }
    }
  );

  const uploadMaterialMutation = useMutation(
    async () => {
      if (!wizardClassworkId || !materialFile) return;
      const typeLabel =
        materialType === 'aufgabenstellung'
          ? 'Aufgabenstellung'
          : materialType === 'rueckgabe'
            ? 'Rückgabedokument'
            : 'Zusätzliche Datei';
      await uploadClassworkMaterial(Number(wizardClassworkId), {
        file: materialFile,
        materialType,
        title: materialTitle.trim() || `${typeLabel}: ${materialFile.name}`,
        isRequired: materialRequired
      });
    },
    {
      onSuccess: () => {
        toast.success('Material hochgeladen');
        setMaterialFile(null);
        setMaterialTitle('');
        setMaterialRequired(false);
        const input = document.getElementById('classwork-material-upload-input') as HTMLInputElement | null;
        if (input) input.value = '';
        void queryClient.invalidateQueries(['classwork-detail', wizardClassworkId]);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Material-Upload fehlgeschlagen');
        setMaterialFile(null);
        const input = document.getElementById('classwork-material-upload-input') as HTMLInputElement | null;
        if (input) input.value = '';
      }
    }
  );

  const createMaterialLinkMutation = useMutation(
    async () => {
      if (!wizardClassworkId || !materialLinkUrl.trim()) return;
      await createClassworkMaterialLink(Number(wizardClassworkId), {
        externalUrl: materialLinkUrl.trim(),
        materialType,
        title: materialLinkTitle.trim() || undefined,
        isRequired: materialRequired
      });
    },
    {
      onSuccess: () => {
        toast.success('Link-Material veröffentlicht');
        setMaterialLinkUrl('');
        setMaterialLinkTitle('');
        setMaterialRequired(false);
        void queryClient.invalidateQueries(['classwork-detail', wizardClassworkId]);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Link konnte nicht erstellt werden');
      }
    }
  );

  const queueOrRerunCorrection = async (
    assignmentId: number,
    opts: { forceRerun?: boolean } = {}
  ): Promise<{ data: any; mode: 'run' | 'rerun' }> => {
    if (!listDetailId) throw new Error('classwork_missing');
    const rows = await fetchClassworkCorrections(Number(listDetailId), assignmentId).catch(() => []);
    const hasActiveCorrection = (rows || []).some((r: any) => Number(r?.is_archived || 0) !== 1);
    const shouldRerun = opts.forceRerun === true || hasActiveCorrection;
    if (shouldRerun) {
      try {
        const data = await rerunCorrection(Number(listDetailId), assignmentId);
        return { data, mode: 'rerun' };
      } catch (error: any) {
        const backendMsg = String(error?.response?.data?.error || '').toLowerCase();
        const archiveNotReady =
          Number(error?.response?.status) === 409 &&
          (backendMsg.includes('migration 139') ||
            backendMsg.includes('ausstehend') ||
            backendMsg.includes('nicht verfuegbar') ||
            backendMsg.includes('nicht verfügbar'));
        if (!archiveNotReady) throw error;
        toast('Archivierte Neukorrektur ist noch nicht aktiv (Migration 139). Starte normalen Korrekturlauf.', {
          duration: 7000
        });
        const data = await queueCorrection(Number(listDetailId), assignmentId);
        return { data, mode: 'run' };
      }
    }
    const data = await queueCorrection(Number(listDetailId), assignmentId);
    return { data, mode: 'run' };
  };

  const runCorrectionWithJobPoll = async (assignmentId: number, opts: { forceRerun?: boolean } = {}) => {
    if (!listDetailId) return;
    setRunningCorrectionsByAssignmentId((prev) => ({ ...prev, [assignmentId]: true }));
    setCorrectionPipelineLabelByAssignmentId((prev) => {
      const next = { ...prev };
      delete next[assignmentId];
      return next;
    });
    const waitForStudentFriendly = async () => {
      for (let i = 0; i < 150; i += 1) {
        await new Promise((r) => setTimeout(r, 1200));
        const rows = await fetchClassworkCorrections(Number(listDetailId), assignmentId);
        const latestCompleted = (rows || []).find((r: any) => String(r?.status || '').toLowerCase() === 'completed');
        if (!latestCompleted) continue;
        const friendlyStatus = String(latestCompleted?.student_friendly_status || '').toLowerCase();
        const friendlyText = String(latestCompleted?.student_friendly_text || '').trim();
        if (friendlyStatus === 'completed' && friendlyText) return 'completed' as const;
        if (friendlyStatus === 'failed') return 'failed' as const;
      }
      return 'timeout' as const;
    };
    try {
      const { data, mode } = await queueOrRerunCorrection(assignmentId, opts);
      const jobId = data?.jobId as string | undefined;
      const refId = data?.reference_set_version_id;
      const archivedCount = Number(data?.archivedCount || 0);
      if (mode === 'rerun') {
        const archivedMsg = archivedCount > 0 ? ` · ${archivedCount} vorherige(s) Ergebnis(se) archiviert` : '';
        toast.success(
          jobId
            ? `Neukorrigieren gestartet …${refId ? ` (Referenz vID ${refId})` : ''}${archivedMsg}`
            : `Neukorrigieren eingereiht${archivedMsg}`
        );
      } else {
        toast.success(jobId ? `Korrektur gestartet …${refId ? ` (Referenz vID ${refId})` : ''}` : 'Korrektur in Warteschlange');
      }
      if (!jobId) {
        queryClient.invalidateQueries(['classwork-detail', listDetailId]);
        return;
      }
      const waitWithSse = () =>
        new Promise<'completed' | 'failed' | 'timeout'>((resolve, reject) => {
          const sse = new EventSource('/api/notifications/stream', { withCredentials: true });
          const timeout = window.setTimeout(() => {
            try {
              sse.close();
            } catch (_) {}
            reject(new Error('sse_timeout'));
          }, 120000);
          sse.onmessage = (event) => {
            try {
              const payload = JSON.parse(event.data || '{}');
              if (payload?.type !== 'job_update') return;
              if (String(payload?.jobId || '') !== String(jobId)) return;
              const st = String(payload?.status || '');
              const step = String((payload as any)?.progressMessage || '').trim();
              if (step) {
                setCorrectionPipelineLabelByAssignmentId((prev) => ({ ...prev, [assignmentId]: step }));
              }
              if (st === 'completed') {
                window.clearTimeout(timeout);
                sse.close();
                resolve('completed');
              } else if (st === 'failed') {
                window.clearTimeout(timeout);
                sse.close();
                resolve('failed');
              } else if (st === 'timeout') {
                window.clearTimeout(timeout);
                sse.close();
                resolve('timeout');
              }
            } catch (_) {}
          };
          sse.onerror = () => {
            window.clearTimeout(timeout);
            try {
              sse.close();
            } catch (_) {}
            reject(new Error('sse_error'));
          };
        });
      try {
        const sseOutcome = await waitWithSse();
        if (sseOutcome === 'completed') {
          toast.success('Detailanalyse fertig – erstelle vereinfachte Rückmeldung …');
          queryClient.invalidateQueries(['classwork-detail', listDetailId]);
          queryClient.invalidateQueries(['classwork-corrections', listDetailId, assignmentId]);
          const friendlyOutcome = await waitForStudentFriendly();
          queryClient.invalidateQueries(['classwork-detail', listDetailId]);
          queryClient.invalidateQueries(['classwork-corrections', listDetailId, assignmentId]);
          if (friendlyOutcome === 'completed') {
            toast.success('Korrektur abgeschlossen');
          } else if (friendlyOutcome === 'failed') {
            toast.error('Vereinfachte Rückmeldung fehlgeschlagen');
          } else {
            toast('Vereinfachte Rückmeldung läuft noch …', { duration: 5000 });
          }
          return;
        }
        if (sseOutcome === 'failed' || sseOutcome === 'timeout') {
          toast.error('Korrektur fehlgeschlagen');
          return;
        }
      } catch {
        // fallback polling
      }
      for (let i = 0; i < 90; i += 1) {
        await new Promise((r) => setTimeout(r, 1500));
        const st = await fetchJobStatus(jobId);
        const pollStep = String((st as any)?.progressMessage || '').trim();
        if (pollStep) {
          setCorrectionPipelineLabelByAssignmentId((prev) => ({ ...prev, [assignmentId]: pollStep }));
        }
        if (st?.status === 'completed') {
          toast.success('Detailanalyse fertig – erstelle vereinfachte Rückmeldung …');
          queryClient.invalidateQueries(['classwork-detail', listDetailId]);
          queryClient.invalidateQueries(['classwork-corrections', listDetailId, assignmentId]);
          const friendlyOutcome = await waitForStudentFriendly();
          queryClient.invalidateQueries(['classwork-detail', listDetailId]);
          queryClient.invalidateQueries(['classwork-corrections', listDetailId, assignmentId]);
          if (friendlyOutcome === 'completed') {
            toast.success('Korrektur abgeschlossen');
          } else if (friendlyOutcome === 'failed') {
            toast.error('Vereinfachte Rückmeldung fehlgeschlagen');
          } else {
            toast('Vereinfachte Rückmeldung läuft noch …', { duration: 5000 });
          }
          return;
        }
        if (st?.status === 'failed' || st?.status === 'timeout') {
          toast.error((st as any)?.error || 'Korrektur fehlgeschlagen');
          return;
        }
      }
      toast('Korrektur läuft noch – Status z. B. über Benachrichtigungen / Job-ID.', { duration: 6000 });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || error?.message || 'Korrekturstart fehlgeschlagen');
    } finally {
      setRunningCorrectionsByAssignmentId((prev) => {
        const next = { ...prev };
        delete next[assignmentId];
        return next;
      });
      setCorrectionPipelineLabelByAssignmentId((prev) => {
        const next = { ...prev };
        delete next[assignmentId];
        return next;
      });
    }
  };

  const runBatchCorrection = async () => {
    if (!listDetailId || isBatchCorrectionRunning) return;
    if (!batchCorrectionCandidateIds.length) {
      toast('Keine unkorrigierten, analysierten Abgaben für Batch-Korrektur gefunden.');
      return;
    }
    setIsBatchCorrectionRunning(true);
    let queued = 0;
    let failed = 0;
    try {
      for (const assignmentId of batchCorrectionCandidateIds) {
        try {
          await queueOrRerunCorrection(assignmentId);
          queued += 1;
        } catch {
          failed += 1;
        }
      }
      if (queued > 0 && failed === 0) {
        toast.success(`${queued} Abgabe(n) zur LLM-Korrektur eingereiht.`);
      } else if (queued > 0 && failed > 0) {
        toast(`${queued} eingereiht, ${failed} fehlgeschlagen.`, { duration: 6000 });
      } else {
        toast.error('Batch-Korrektur konnte nicht gestartet werden.');
      }
    } finally {
      setIsBatchCorrectionRunning(false);
      void queryClient.invalidateQueries(['classwork-detail', listDetailId]);
    }
  };

  const runBatchPublish = async () => {
    if (!listDetailId || isBatchPublishRunning) return;
    if (!batchPublishCandidateIds.length) {
      toast('Keine bewerteten, unveröffentlichten Noten gefunden.');
      return;
    }
    const ok = window.confirm(`Wirklich ${batchPublishCandidateIds.length} Note(n) auf einmal veröffentlichen?`);
    if (!ok) return;
    setIsBatchPublishRunning(true);
    let published = 0;
    let createdCompetencies = 0;
    let failed = 0;
    try {
      for (const assignmentId of batchPublishCandidateIds) {
        try {
          const out = await publishGrade(Number(listDetailId), assignmentId);
          if (out?.published === false) {
            failed += 1;
            continue;
          }
          published += 1;
          createdCompetencies += Math.max(0, Number(out?.evidenceSync?.created || 0));
        } catch {
          failed += 1;
        }
      }
      if (published > 0 && failed === 0) {
        toast.success(`${published} Noten veröffentlicht · ${createdCompetencies} Kompetenzen vergeben.`);
      } else if (published > 0 && failed > 0) {
        toast(`${published} Noten veröffentlicht · ${createdCompetencies} Kompetenzen vergeben · ${failed} fehlgeschlagen.`, {
          duration: 6000
        });
      } else {
        toast.error('Keine Note konnte veröffentlicht werden.');
      }
    } finally {
      setIsBatchPublishRunning(false);
      void queryClient.invalidateQueries(['classwork-detail', listDetailId]);
    }
  };

  const assignments = useMemo(() => listDetailQuery.data?.assignments || [], [listDetailQuery.data]);
  const toParticipantOptions = useCallback((rows: ClassworkAssignment[] = []) => {
    return rows
      .map((a: ClassworkAssignment) => {
        const sid = String(a.student_id || '').trim();
        if (!sid) return null;
        const last = String(a.last_name || '').trim();
        const first = String(a.first_name || '').trim();
        const cls = String(a.class_name || '').trim();
        const name = [last, first].filter(Boolean).join(', ') || sid;
        return { student_id: sid, label: cls ? `${name} · ${cls}` : name };
      })
      .filter(Boolean) as Array<{ student_id: string; label: string }>;
  }, []);
  const wizardParticipantOptions = useMemo(
    () => toParticipantOptions((wizardDetailQuery.data?.assignments || []) as ClassworkAssignment[]),
    [wizardDetailQuery.data?.assignments, toParticipantOptions]
  );
  const listParticipantOptions = useMemo(
    () => toParticipantOptions((assignments || []) as ClassworkAssignment[]),
    [assignments, toParticipantOptions]
  );
  const diagnosticsQuery = useQuery(
    ['classwork-analysis-diagnostics', listDetailId],
    () => fetchClassworkAnalysisDiagnostics(Number(listDetailId)),
    { enabled: Boolean(listDetailId), retry: false }
  );
  const pipelineDecisionsQuery = useQuery(
    ['classwork-pipeline-decisions', listDetailId],
    () => fetchClassworkPipelineDecisions(Number(listDetailId), 120),
    { enabled: Boolean(listDetailId), retry: false }
  );
  const pipelineMetricsQuery = useQuery(
    ['classwork-pipeline-metrics', listDetailId],
    () => fetchClassworkPipelineMetrics(Number(listDetailId), 500),
    { enabled: Boolean(listDetailId), retry: false }
  );
  const pipelineReviewReasonsQuery = useQuery(
    ['classwork-pipeline-review-reasons', listDetailId],
    () => fetchClassworkPipelineReviewReasons(Number(listDetailId)),
    { enabled: Boolean(listDetailId), retry: false }
  );
  const pipelineDecisionItems = Array.isArray(pipelineDecisionsQuery.data?.items)
    ? pipelineDecisionsQuery.data.items
    : [];
  const pipelineMetricItems = Array.isArray(pipelineMetricsQuery.data?.items)
    ? pipelineMetricsQuery.data.items
    : [];
  const pipelineReviewReasonItems = Array.isArray(pipelineReviewReasonsQuery.data?.items)
    ? pipelineReviewReasonsQuery.data.items
    : [];
  const latestPolicyDecision = pipelineDecisionItems.find(
    (item: any) => String(item?.stage_name || '').toLowerCase() === 'correction_policy'
  ) || pipelineDecisionItems[0] || null;
  const autoGradeCount = pipelineDecisionItems.filter(
    (item: any) => String(item?.decision_name || '').toUpperCase().startsWith('AUTO_GRADE')
  ).length;
  const humanReviewCount = pipelineDecisionItems.filter(
    (item: any) => String(item?.decision_name || '').toUpperCase().includes('HUMAN_REVIEW')
  ).length;
  const repairCount = pipelineDecisionItems.filter(
    (item: any) => String(item?.decision_name || '').toUpperCase().includes('REPAIR')
  ).length;
  const scorerDivergenceMetric = pipelineMetricItems.find(
    (item: any) => String(item?.metric_key || '') === 'scorer_divergence'
  );

  const selectedCorrectionSubtitle = useMemo(() => {
    if (!selectedAssignmentId) return undefined;
    const a = assignments.find((x: ClassworkAssignment) => {
      const raw = x.id ?? x.assignment_id;
      return Number(raw) === selectedAssignmentId;
    });
    if (!a) return `Zuweisung #${selectedAssignmentId}`;
    const name =
      a.last_name || a.first_name
        ? `${a.last_name || ''}${a.last_name && a.first_name ? ', ' : ''}${a.first_name || ''}`
        : a.student_id;
    return `${name} · ${a.class_name || '—'} · ${a.student_id}`;
  }, [assignments, selectedAssignmentId]);
  const classOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (teachingMetaQuery.data?.classes || [])
            .map((c) => String(c.class_name || '').trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, 'de')),
    [teachingMetaQuery.data?.classes]
  );
  const filteredManualStudents = useMemo(() => {
    const q = manualQuery.trim().toLowerCase();
    const rows = studentDirectoryQuery.data || [];
    if (!q) return rows.slice(0, 120);
    return rows
      .filter((s) => {
        const unid = String(s.sophomorixUnid || '').toLowerCase();
        const firstName = String(s.Vorname || '').toLowerCase();
        const lastName = String(s.Name || '').toLowerCase();
        const klasse = String(s.Klasse || '').toLowerCase();
        return unid.includes(q) || firstName.includes(q) || lastName.includes(q) || klasse.includes(q);
      })
      .slice(0, 120);
  }, [studentDirectoryQuery.data, manualQuery]);

  const onlyofficeWarningsByAssignmentId = useMemo(() => {
    const out: Record<number, string> = {};
    const subs = listDetailQuery.data?.submissions || [];
    for (const s of subs) {
      if (String(s?.source || '') !== 'onlyoffice') continue;
      let meta: any = s?.metadata;
      if (typeof meta === 'string') {
        try {
          meta = JSON.parse(meta);
        } catch {
          meta = null;
        }
      }
      const oo = meta?.onlyoffice;
      if (!oo || String(oo.callbackStatus || '') !== 'failed') continue;
      const aid = Number(s?.assignment_id);
      if (!aid) continue;
      const when = oo?.lastErrorAt ? ` (${new Date(oo.lastErrorAt).toLocaleString()})` : '';
      out[aid] = `OnlyOffice-Callback fehlgeschlagen${when}`;
    }
    return out;
  }, [listDetailQuery.data]);

  const latestSubmissionByAssignmentId = useMemo(() => {
    const preferred: Record<number, any> = {};
    const subs = listDetailQuery.data?.submissions || [];
    for (const s of subs) {
      const aid = Number(s?.assignment_id);
      if (!aid) continue;
      const state = String(s?.state || '').toLowerCase();
      if (!preferred[aid] && ['submitted', 'locked'].includes(state)) preferred[aid] = s;
    }
    return preferred;
  }, [listDetailQuery.data?.submissions]);

  const archivedSubmissionsByAssignmentId = useMemo(() => {
    const out: Record<number, any[]> = {};
    const subs = listDetailQuery.data?.submissions || [];
    for (const s of subs) {
      const aid = Number(s?.assignment_id);
      if (!aid) continue;
      const state = String(s?.state || '').toLowerCase();
      if (!['invalid', 'replaced'].includes(state)) continue;
      if (!out[aid]) out[aid] = [];
      out[aid].push(s);
    }
    return out;
  }, [listDetailQuery.data?.submissions]);

  const batchCorrectionCandidateIds = useMemo(() => {
    const ids: number[] = [];
    for (const a of assignments) {
      const assignmentId = Number((a as any)?.id ?? (a as any)?.assignment_id);
      if (!Number.isFinite(assignmentId) || assignmentId <= 0) continue;
      const state = String(a?.state || '').toLowerCase();
      if (!['submitted', 'reviewed'].includes(state)) continue;
      const latestSubmission = latestSubmissionByAssignmentId[assignmentId];
      const analysisStatus = String(latestSubmission?.analysis_status || '').toLowerCase();
      if (!latestSubmission || !['completed', 'failed'].includes(analysisStatus)) continue;
      ids.push(assignmentId);
    }
    return ids;
  }, [assignments, latestSubmissionByAssignmentId]);

  const batchPublishCandidateIds = useMemo(() => {
    const ids: number[] = [];
    for (const a of assignments) {
      const assignmentId = Number((a as any)?.id ?? (a as any)?.assignment_id);
      if (!Number.isFinite(assignmentId) || assignmentId <= 0) continue;
      const state = String(a?.state || '').toLowerCase();
      if (state !== 'graded') continue;
      ids.push(assignmentId);
    }
    return ids;
  }, [assignments]);

  const autoAnalyzeCandidates = useMemo(() => {
    const rows: Array<{ assignmentId: number; submissionId: number }> = [];
    for (const assignmentId of batchCorrectionCandidateIds) {
      const sub = latestSubmissionByAssignmentId[assignmentId];
      const submissionId = Number(sub?.id);
      if (!Number.isFinite(submissionId) || submissionId <= 0) continue;
      rows.push({ assignmentId, submissionId });
    }
    return rows;
  }, [batchCorrectionCandidateIds, latestSubmissionByAssignmentId]);

  useEffect(() => {
    if (!autoAnalyzeNewSubmissions || !listDetailId || isAutoAnalyzeTickRunning) return;
    const pending = autoAnalyzeCandidates.filter(
      ({ assignmentId, submissionId }) => autoAnalyzeLastSubmissionByAssignmentRef.current[assignmentId] !== submissionId
    );
    if (!pending.length) return;
    let cancelled = false;
    const run = async () => {
      setIsAutoAnalyzeTickRunning(true);
      let queued = 0;
      for (const { assignmentId, submissionId } of pending) {
        if (cancelled) break;
        try {
          await queueOrRerunCorrection(assignmentId);
          autoAnalyzeLastSubmissionByAssignmentRef.current[assignmentId] = submissionId;
          queued += 1;
        } catch {
          // on failure we keep it pending and retry on next tick
        }
      }
      if (!cancelled && queued > 0) {
        toast.success(`Auto-Analyse: ${queued} Neueinreichung(en) eingereiht.`);
        void queryClient.invalidateQueries(['classwork-detail', listDetailId]);
      }
      if (!cancelled) setIsAutoAnalyzeTickRunning(false);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [autoAnalyzeNewSubmissions, autoAnalyzeCandidates, isAutoAnalyzeTickRunning, listDetailId, queryClient]);

  const renderBatchActions = () => (
    <div className="rounded-xl border-2 border-primary-200 bg-gradient-to-r from-primary-50 to-indigo-50 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-primary-900">Schnellaktionen für Abgaben</div>
          <div className="text-xs text-primary-800">
            Unkorrigiert: {batchCorrectionCandidateIds.length} · Veröffentlichbar: {batchPublishCandidateIds.length}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 rounded-full border border-blue-300 bg-white px-3 py-2 text-xs font-medium text-blue-900">
            <input
              type="checkbox"
              checked={autoAnalyzeNewSubmissions}
              onChange={(e) => setAutoAnalyzeNewSubmissions(e.target.checked)}
            />
            Alle Neueinreichungen automatisch analysieren
            {isAutoAnalyzeTickRunning ? <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-700" /> : null}
          </label>
          <button
            type="button"
            className="btn-primary text-sm font-semibold shadow"
            onClick={() => void runBatchCorrection()}
            disabled={isBatchCorrectionRunning || !batchCorrectionCandidateIds.length}
            title={!batchCorrectionCandidateIds.length ? 'Keine unkorrigierten Abgaben mit fertiger Analyse' : undefined}
          >
            {isBatchCorrectionRunning
              ? 'Batch-Korrektur läuft …'
              : `Alle unkorrigierten im Batch korrigieren (${batchCorrectionCandidateIds.length})`}
          </button>
          <button
            type="button"
            className="btn-secondary text-sm font-semibold"
            onClick={() => void runBatchPublish()}
            disabled={isBatchPublishRunning || !batchPublishCandidateIds.length}
            title={!batchPublishCandidateIds.length ? 'Keine bewerteten, unveröffentlichten Noten vorhanden' : undefined}
          >
            {isBatchPublishRunning ? 'Veröffentlichung läuft …' : `Alle Noten veröffentlichen (${batchPublishCandidateIds.length})`}
          </button>
        </div>
      </div>
    </div>
  );

  const handleArchiveSubmission = useCallback(
    (assignmentId: number) => {
      const id = listDetailId;
      if (!id) return;
      const ok = window.confirm('Abgabe archivieren und für diesen Schüler eine neue Einreichung erlauben?');
      if (!ok) return;
      archiveSubmissionForResubmission(Number(id), assignmentId)
        .then(() => {
          toast.success('Abgabe archiviert – Neueinreichung freigegeben');
          queryClient.invalidateQueries(['classwork-detail', id]);
        })
        .catch((error: any) => {
          toast.error(error?.response?.data?.error || 'Archivieren fehlgeschlagen');
        });
    },
    [listDetailId, queryClient]
  );

  const handleRestoreSubmission = useCallback(
    (_assignmentId: number, submissionId: number) => {
      const id = listDetailId;
      if (!id) return;
      const ok = window.confirm(
        'Archivierte Einreichung wieder einsetzen? Falls eine neuere aktive Abgabe existiert, wird diese automatisch archiviert.'
      );
      if (!ok) return;
      restoreArchivedSubmission(Number(id), submissionId)
        .then(() => {
          toast.success('Archivierte Einreichung wurde wieder eingesetzt');
          queryClient.invalidateQueries(['classwork-detail', id]);
        })
        .catch((error: any) => {
          toast.error(error?.response?.data?.error || 'Wieder-Einsetzen fehlgeschlagen');
        });
    },
    [listDetailId, queryClient]
  );

  const handleRestoreCorrection = useCallback(
    (assignmentId: number, correctionId: number) => {
      const id = listDetailId;
      if (!id) return Promise.resolve();
      return restoreArchivedCorrection(Number(id), assignmentId, correctionId)
        .then(() => {
          toast.success('Archivierter Korrekturlauf wurde wiederhergestellt');
          queryClient.invalidateQueries(['classwork-detail', id]);
          queryClient.invalidateQueries(['classwork-corrections', id, assignmentId]);
        })
        .catch((error: any) => {
          toast.error(error?.response?.data?.error || 'Wiederherstellen fehlgeschlagen');
        });
    },
    [listDetailId, queryClient]
  );

  const gradeRowByAssignmentId = useMemo(() => {
    const out: Record<number, any> = {};
    const grades = listDetailQuery.data?.grades || [];
    for (const g of grades) {
      const aid = Number(g?.assignment_id);
      if (!aid) continue;
      const prev = out[aid];
      if (!prev || Number(g.id) > Number(prev.id)) out[aid] = g;
    }
    return out;
  }, [listDetailQuery.data?.grades]);

  /** Neueste classwork_corrections-Zeile pro assignment_id (vom Detail-Endpunkt) */
  const latestCorrectionByAssignmentId = useMemo(() => {
    const out: Record<number, { id?: number; status?: string; pipeline_status?: string | null }> = {};
    const rows = (listDetailQuery.data as any)?.latest_corrections || [];
    for (const r of rows) {
      const aid = Number(r?.assignment_id);
      if (!aid) continue;
      out[aid] = {
        id: r?.id != null ? Number(r.id) : undefined,
        status: r?.status != null ? String(r.status) : undefined,
        pipeline_status: r?.pipeline_status != null && r?.pipeline_status !== '' ? String(r.pipeline_status) : null
      };
    }
    return out;
  }, [listDetailQuery.data]);

  const wizardBoundSchemaName = useMemo(() => {
    const raw = wizardDetailQuery.data?.item?.grade_schema_id ?? wGradeSchemaId;
    if (raw == null || raw === '' || !Number(raw)) return null;
    const row = (wizardGradeSchemasQuery.data || []).find((s) => Number(s.id) === Number(raw));
    return row?.name || `Schema #${raw}`;
  }, [wizardDetailQuery.data?.item?.grade_schema_id, wGradeSchemaId, wizardGradeSchemasQuery.data]);

  const wizardMaterials = useMemo(() => wizardDetailQuery.data?.materials || [], [wizardDetailQuery.data?.materials]);
  const expectationHorizonMaterials = useMemo(
    () => wizardMaterials.filter((m: any) => isExpectationHorizonMaterial(m)),
    [wizardMaterials]
  );
  const sampleSolutionMaterials = useMemo(
    () => wizardMaterials.filter((m: any) => isMusterloesungMaterial(m)),
    [wizardMaterials]
  );

  const listDetailMaterials = useMemo(() => listDetailQuery.data?.materials || [], [listDetailQuery.data?.materials]);
  const referenceVersions = useMemo(
    () => (referenceVersionsQuery.data?.versions || []) as ClassworkReferenceVersion[],
    [referenceVersionsQuery.data?.versions]
  );
  const activeReferenceVersion = useMemo(
    () => referenceVersionsQuery.data?.activeReferenceVersion || null,
    [referenceVersionsQuery.data?.activeReferenceVersion]
  );
  const hasBuildingReferenceVersion = useMemo(
    () => referenceVersions.some((v) => String(v?.status || '') === 'building'),
    [referenceVersions]
  );
  const listExpectationHorizonFiles = useMemo(
    () => listDetailMaterials.filter((m: any) => isExpectationHorizonMaterial(m)),
    [listDetailMaterials]
  );
  const listMusterloesungFiles = useMemo(
    () => listDetailMaterials.filter((m: any) => isMusterloesungMaterial(m)),
    [listDetailMaterials]
  );

  /** Genau eine digitale Option aktiv; Altbestände können ambiguous sein. */
  const digitalSubmissionMode = useMemo<'onlyoffice' | 'tiptap' | 'upload' | 'ambiguous' | 'none'>(() => {
    const item = wizardDetailQuery.data?.item;
    const oo = Boolean(Number(item?.onlyoffice_enabled ?? 0));
    const tt = Boolean(Number(item?.tiptap_enabled ?? 0));
    const up = Boolean(Number(item?.student_file_upload_enabled ?? 0));
    const n = [oo, tt, up].filter(Boolean).length;
    if (n > 1) return 'ambiguous';
    if (n === 0) return 'none';
    if (up) return 'upload';
    if (oo) return 'onlyoffice';
    return 'tiptap';
  }, [
    wizardDetailQuery.data?.item?.onlyoffice_enabled,
    wizardDetailQuery.data?.item?.tiptap_enabled,
    wizardDetailQuery.data?.item?.student_file_upload_enabled
  ]);

  const wizardReturnMode = useMemo<ReturnModeChoice>(() => resolveReturnMode(wizardDetailQuery.data?.item), [wizardDetailQuery.data?.item]);
  const isWizardFileUploadMode = wizardReturnMode === 'digital_system' && digitalSubmissionChoice === 'upload';
  const listDetailReturnMode = useMemo<ReturnModeChoice>(() => resolveReturnMode(listDetailQuery.data?.item), [listDetailQuery.data?.item]);
  const listDetailItem = listDetailQuery.data?.item;
  const detailStatusBadge = classworkStatusBadge(listDetailItem?.status);
  const detailMetrics = useMemo(() => {
    const total = assignments.length;
    let submitted = 0;
    let graded = 0;
    let published = 0;
    let runningAnalysis = 0;
    for (const a of assignments) {
      const state = String((a as any)?.state || '').toLowerCase();
      const assignmentId = Number((a as any)?.id ?? (a as any)?.assignment_id);
      const latestSubmission = Number.isFinite(assignmentId) ? latestSubmissionByAssignmentId[assignmentId] : null;
      const analysisStatus = String(latestSubmission?.analysis_status || '').toLowerCase();
      if (state === 'submitted' || state === 'reviewed' || state === 'locked' || state === 'graded' || state === 'published') submitted += 1;
      if (state === 'graded' || state === 'published') graded += 1;
      if (state === 'published') published += 1;
      if (analysisStatus === 'pending' || analysisStatus === 'running') runningAnalysis += 1;
    }
    return { total, submitted, graded, published, runningAnalysis };
  }, [assignments, latestSubmissionByAssignmentId]);
  const detailPrimaryAction = useMemo(() => {
    if (String(listDetailItem?.status || '').toLowerCase() === 'draft') {
      return { label: 'Konzeption fertigstellen', targetId: null as string | null, type: 'edit' as const };
    }
    if (listDetailReturnMode === 'paper_presence' || listDetailReturnMode === 'results_import_only') {
      return { label: 'Rückläufe öffnen', targetId: 'cw-section-returns', type: 'scroll' as const };
    }
    if (detailMetrics.submitted > 0) {
      return { label: 'Korrektur starten', targetId: 'cw-section-correction', type: 'scroll' as const };
    }
    return { label: 'Abgaben ansehen', targetId: 'cw-section-returns', type: 'scroll' as const };
  }, [detailMetrics.submitted, listDetailItem?.status, listDetailReturnMode]);

  useEffect(() => {
    if (digitalSubmissionMode === 'onlyoffice' || digitalSubmissionMode === 'tiptap' || digitalSubmissionMode === 'upload') {
      setDigitalSubmissionChoice(digitalSubmissionMode);
    }
  }, [digitalSubmissionMode]);

  const backgroundJobsQuery = useQuery(
    ['classwork-background-jobs'],
    async () => {
      const response = await fetch('/api/jobs?limit=50&offset=0', { credentials: 'include' });
      if (!response.ok) throw new Error(`http_${response.status}`);
      const payload = await response.json();
      return Array.isArray(payload?.data?.jobs) ? payload.data.jobs : [];
    },
    { refetchInterval: 10000, refetchOnWindowFocus: false }
  );

  const runningClassworkJobs = useMemo(
    () =>
      (backgroundJobsQuery.data || []).filter(
        (job: any) =>
          String(job?.type || '').startsWith('classwork_') &&
          ['pending', 'processing'].includes(String(job?.status || ''))
      ),
    [backgroundJobsQuery.data]
  );

  const correctionPipelineFromJobsByAssignmentId = useMemo(() => {
    const out: Record<number, string> = {};
    const jobs = (backgroundJobsQuery.data || []) as any[];
    if (!jobs.length) return out;
    const assignmentIdByCorrectionId = new Map<number, number>();
    for (const [assignmentIdRaw, corr] of Object.entries(latestCorrectionByAssignmentId || {})) {
      const aid = Number(assignmentIdRaw);
      const cid = Number((corr as any)?.id);
      if (Number.isFinite(aid) && aid > 0 && Number.isFinite(cid) && cid > 0) {
        assignmentIdByCorrectionId.set(cid, aid);
      }
    }
    for (const job of jobs) {
      if (String(job?.type || '') !== 'classwork_correction') continue;
      const status = String(job?.status || '').toLowerCase();
      if (!(status === 'pending' || status === 'processing')) continue;
      const correctionId = Number(job?.correctionId ?? job?.data?.correctionId);
      if (!Number.isFinite(correctionId) || correctionId <= 0) continue;
      const assignmentId = assignmentIdByCorrectionId.get(correctionId);
      if (!assignmentId) continue;
      const message = String(job?.progressMessage || '').trim();
      if (!message) continue;
      out[assignmentId] = message;
    }
    return out;
  }, [backgroundJobsQuery.data, latestCorrectionByAssignmentId]);

  const mergedCorrectionPipelineLabelByAssignmentId = useMemo(
    () => ({
      ...correctionPipelineFromJobsByAssignmentId,
      ...correctionPipelineLabelByAssignmentId
    }),
    [correctionPipelineFromJobsByAssignmentId, correctionPipelineLabelByAssignmentId]
  );

  useEffect(() => {
    const es = new EventSource('/api/notifications/stream', { withCredentials: true });
    es.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data || '{}');
        if (msg?.type === 'job_update') {
          void queryClient.invalidateQueries(['classwork-background-jobs']);
        }
      } catch {
        // ignore malformed SSE messages in UI helper
      }
    };
    return () => {
      try {
        es.close();
      } catch {
        // ignore cleanup errors
      }
    };
  }, [queryClient]);

  const steps: Array<{ id: 1 | 2 | 3 | 4 | 5; title: string; subtitle: string }> = [
    { id: 1, title: 'Rahmen festlegen', subtitle: 'Titel, Fach, Curriculum' },
    { id: 2, title: 'Aufgabe hinzufügen', subtitle: 'Aufgabe, Dateien, Links' },
    { id: 3, title: 'Bewertung festlegen', subtitle: 'Schema, Kompetenzen, Korrekturraster' },
    { id: 4, title: 'Durchführung wählen', subtitle: 'Rücklaufmodus und Zeitfenster' },
    { id: 5, title: 'Veröffentlichen', subtitle: 'Teilnehmer zuweisen und starten' }
  ];

  const saveDraft = async () => {
    if (!wizardClassworkId) {
      toast.error('Bitte zuerst Schritt 1 abschließen');
      return;
    }
    try {
      const saved = await patchWizard({
        title: wTitle.trim(),
        description: wDescription.trim() || null,
        subject_id: wSubjectId ? Number(wSubjectId) : null,
        curriculum_id: wCurriculumId ? Number(wCurriculumId) : null,
        estimated_duration_minutes: wEstimatedMinutes.trim() ? Math.min(600, Math.max(1, Number(wEstimatedMinutes))) : null,
        starts_at: localDatetimeToIso(wStartsAt),
        due_at: localDatetimeToIso(wDueAt),
        latest_submit_at: localDatetimeToIso(wLatestSubmitAt),
        max_attempts: Math.min(20, Math.max(1, parseInt(wMaxAttempts, 10) || 1)),
        seb_mode: wSebMode,
        expectation_horizon: wExpectation.trim() || null,
        sample_solution: wSample.trim() || null,
        grade_schema_id: wGradeSchemaId ? Number(wGradeSchemaId) : null
      });
      if (!saved) return;
      toast.success('Entwurf gespeichert');
    } catch {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const handleStep1Next = async () => {
    if (!wTitle.trim()) {
      toast.error('Bitte einen Titel eingeben');
      return;
    }
    try {
      let cwId = wizardClassworkId;
      if (!cwId) {
        const data = await createClasswork({
          title: wTitle.trim(),
          description: undefined,
          entry_type: 'classwork_digital',
          seb_mode: 'seb_optional',
          max_attempts: 1,
          status: 'draft',
          teaching_unit_id: linkedTeachingUnitId ?? undefined,
          subject_id: wSubjectId ? Number(wSubjectId) : undefined,
          curriculum_id: wCurriculumId ? Number(wCurriculumId) : undefined
        } as any);
        cwId = Number(data?.id);
        if (!cwId) throw new Error('no_id');
        setWizardClassworkId(cwId);
        await queryClient.invalidateQueries(['classwork-list']);
      } else {
        const step1Payload = {
          title: wTitle.trim(),
          subject_id: wSubjectId ? Number(wSubjectId) : null,
          curriculum_id: wCurriculumId ? Number(wCurriculumId) : null
        } as any;
        if (hasPayloadChangesAgainstClasswork(step1Payload, wizardDetailQuery.data?.item)) {
          const recorrectionCount = estimateRecorrectionCandidates(
            wizardDetailQuery.data?.assignments || [],
            wizardDetailQuery.data?.submissions || []
          );
          if (recorrectionCount > 0) {
            const ok = window.confirm(
              `Die Klassenarbeit wurde verändert. ${recorrectionCount} bereits bearbeitete Arbeit(en) müssten ggf. neu korrigiert werden.\n\nMöchtest du trotzdem speichern?`
            );
            if (!ok) return;
          }
        }
        await updateClasswork(cwId, step1Payload);
        await queryClient.invalidateQueries(['classwork-detail', cwId]);
      }
      setWizardStep(2);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Schritt 1 konnte nicht gespeichert werden');
    }
  };

  const handleFinalize = async () => {
    if (!wizardClassworkId) return;
    const schemaId = Number(wizardDetailQuery.data?.item?.grade_schema_id ?? wGradeSchemaId);
    if (!Number.isFinite(schemaId) || schemaId < 1) {
      toast.error('Notenschema fehlt. Bitte Schritt 3 öffnen, ein Schema wählen und mit „Weiter“ speichern.');
      setWizardStep(3);
      return;
    }
    try {
      await assignByScopeMutation.mutateAsync(wizardClassworkId);
      await updateClasswork(wizardClassworkId, { status: 'active' } as any);
      toast.success('Klassenarbeit ist aktiv');
      queryClient.invalidateQueries(['classwork-list']);
      queryClient.invalidateQueries(['classwork-detail', wizardClassworkId]);
      setMainTab('list');
      resetWizard();
    } catch {
      toast.error('Aktivieren fehlgeschlagen');
    }
  };

  const activeCwForModal = wizardClassworkId || listDetailId;

  const runBuildReferenceVersion = async () => {
    const id = listDetailId;
    if (!id) return;
    setIsBuildingReferenceVersion(true);
    try {
      const data = await buildReferenceVersion(Number(id));
      const jobId = data?.jobId as string | undefined;
      toast.success('Referenzversion wird erstellt …');
      if (jobId) {
        for (let i = 0; i < 120; i += 1) {
          await new Promise((r) => setTimeout(r, 1500));
          const st = await fetchJobStatus(jobId);
          if (st?.status === 'completed') {
            toast.success('Referenzversion erstellt');
            void queryClient.invalidateQueries(['classwork-reference-versions', id]);
            void queryClient.invalidateQueries(['classwork-detail', id]);
            return;
          }
          if (st?.status === 'failed' || st?.status === 'timeout') {
            toast.error((st as any)?.error || 'Build der Referenzversion fehlgeschlagen');
            return;
          }
        }
        toast('Build läuft noch – Jobstatus in Benachrichtigungen prüfen.', { duration: 5000 });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Referenzversion konnte nicht gestartet werden');
    } finally {
      setIsBuildingReferenceVersion(false);
    }
  };

  const runActivateReferenceVersion = async (versionId: number) => {
    const id = listDetailId;
    if (!id) return;
    setIsActivatingReferenceVersion(versionId);
    try {
      await activateReferenceVersion(Number(id), versionId);
      toast.success('Referenzversion aktiviert');
      void queryClient.invalidateQueries(['classwork-reference-versions', id]);
      void queryClient.invalidateQueries(['classwork-detail', id]);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Aktivierung fehlgeschlagen');
    } finally {
      setIsActivatingReferenceVersion(null);
    }
  };

  const runCancelReferenceVersionBuild = async () => {
    const id = listDetailId;
    if (!id) return;
    if (!window.confirm('Laufenden Referenz-Build wirklich abbrechen?')) return;
    setIsCancellingReferenceVersionBuild(true);
    try {
      await cancelReferenceVersionBuild(Number(id));
      toast.success('Referenz-Build abgebrochen');
      void queryClient.invalidateQueries(['classwork-reference-versions', id]);
      void queryClient.invalidateQueries(['classwork-detail', id]);
      void queryClient.invalidateQueries(['classwork-background-jobs']);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Build-Abbruch fehlgeschlagen');
    } finally {
      setIsCancellingReferenceVersionBuild(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Klassenarbeit</h1>
            <p className="mt-2 text-gray-600">Übersicht oder neue Klassenarbeit anlegen.</p>
          </div>
          {linkedTeachingUnitId ? (
            <Link
              to="/unterrichten"
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              ← Zurück zu Unterrichten
            </Link>
          ) : null}
        </div>

        {linkedTeachingUnitId ? (
          <div className="rounded-lg border border-primary-100 bg-primary-50/80 px-4 py-3 text-sm text-primary-900">
            {teachingUnitQuery.isLoading && <p>Unterrichtseinheit wird geladen …</p>}
            {teachingUnitQuery.data?.unit && (
              <p>
                <span className="font-semibold">Kontext:</span> {teachingUnitQuery.data.unit.title}
              </p>
            )}
          </div>
        ) : null}

        {runningClassworkJobs.length ? (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/70 px-3 py-2 text-sm text-indigo-900">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p>
                <span className="font-semibold">{runningClassworkJobs.length}</span> Analyse(n) laufen im Hintergrund.
              </p>
              <button
                type="button"
                className="text-xs font-medium underline"
                onClick={() => setShowBackgroundJobs((prev) => !prev)}
              >
                {showBackgroundJobs ? 'Ausblenden' : 'Details anzeigen'}
              </button>
            </div>
            {showBackgroundJobs ? (
              <ul className="mt-2 space-y-1 text-xs border-t border-indigo-200/80 pt-2">
                {runningClassworkJobs.slice(0, 6).map((job: any) => (
                  <li key={job.id} className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{String(job.type || '').replace(/^classwork_/, '')}</span>
                    <span className="rounded border border-indigo-300 bg-white/70 px-1.5 py-0.5">
                      {String(job.status || '')}
                    </span>
                    <span>{Number(job.progress || 0)}%</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white shadow-sm max-w-lg">
          <button
            type="button"
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium ${
              mainTab === 'list' ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => setMainTab('list')}
          >
            <LayoutGrid className="h-4 w-4 flex-shrink-0" aria-hidden />
            Übersicht
          </button>
          <button
            type="button"
            className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium ${
              mainTab === 'create' ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => {
              setMainTab('create');
              setWizardStep(1);
            }}
          >
            <PlusCircle className="h-4 w-4 flex-shrink-0" aria-hidden />
            Neu anlegen
          </button>
        </div>

        {mainTab === 'list' ? (
          <div className="space-y-6">
            <div>
              <div className="mb-3">
                <h2 className="text-lg font-semibold text-gray-900">Klassenarbeiten</h2>
                <p className="text-xs text-gray-500">Arbeitsliste mit klarer Nächster-Aktion pro Klassenarbeit.</p>
              </div>
              {!listQuery.data?.length ? (
                <div className="rounded-lg border border-dashed border-gray-200 bg-white px-6 py-10 text-center text-sm text-gray-500">
                  Noch keine Klassenarbeiten.
                </div>
              ) : (
                <div className="space-y-3">
                  {(listQuery.data || []).map((item: any) => {
                    const badge = classworkStatusBadge(item.status);
                    const mode = resolveReturnMode(item);
                    const selected = listDetailId === item.id;
                    const status = String(item.status || '').toLowerCase();
                    const nextActionLabel =
                      status === 'draft'
                        ? 'Weiter bearbeiten'
                        : mode === 'paper_presence'
                          ? 'Rückläufe einsammeln'
                          : mode === 'results_import_only'
                            ? 'Ergebnisse importieren'
                            : 'Abgaben prüfen';
                    return (
                      <div
                        key={item.id}
                        className={`bg-white rounded-lg border shadow-sm hover:shadow-md transition-all ${
                          selected ? 'ring-2 ring-primary-400 border-primary-200' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-stretch">
                          <div
                            className={`flex-shrink-0 w-10 sm:w-11 flex items-center justify-center rounded-l-lg ${
                              selected
                                ? 'bg-gradient-to-b from-primary-600 to-primary-700'
                                : 'bg-gradient-to-b from-slate-500 to-slate-600'
                            }`}
                          >
                            <FileText className="h-5 w-5 text-white drop-shadow-sm" aria-hidden />
                          </div>
                          <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4">
                            <button
                              type="button"
                              className="text-left min-w-0 flex-1 group"
                              onClick={() => setListDetailId(listDetailId === item.id ? null : item.id)}
                            >
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-gray-900 truncate group-hover:text-primary-800">
                                  {item.title}
                                </span>
                                <ChevronRight
                                  className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${
                                    selected ? 'rotate-90 text-primary-600' : ''
                                  }`}
                                  aria-hidden
                                />
                              </div>
                              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.className}`}
                                >
                                  {badge.label}
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-800 border border-indigo-200">
                                  {entryTypeShort(item.entry_type)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {classworkProgressLabel(item)}
                                </span>
                              </div>
                            </button>
                            <div
                              className="flex flex-col items-end gap-2 flex-shrink-0 border-t border-gray-100 pt-2 sm:pt-0 sm:border-t-0 sm:pl-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => {
                                  if (status === 'draft') {
                                    openClassworkForEdit(Number(item.id));
                                    return;
                                  }
                                  setListDetailId(item.id);
                                }}
                              >
                                {nextActionLabel}
                              </button>
                              <div className="flex items-center justify-end gap-0.5 sm:gap-1">
                              <button
                                type="button"
                                title="Bearbeiten"
                                className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                onClick={() => openClassworkForEdit(Number(item.id))}
                              >
                                <Pencil className="h-4 w-4" />
                                <span className="sr-only">Bearbeiten</span>
                              </button>
                              <button
                                type="button"
                                title="Duplizieren"
                                className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                onClick={() => duplicateMutation.mutate(item.id)}
                                disabled={duplicateMutation.isLoading}
                              >
                                <Copy className="h-4 w-4" />
                                <span className="sr-only">Duplizieren</span>
                              </button>
                              <button
                                type="button"
                                title="Für Schüler sichtbar schalten"
                                className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                onClick={() =>
                                  updateClasswork(item.id, { status: 'active' } as any)
                                    .then(() => toast.success('Sichtbar'))
                                    .then(() => queryClient.invalidateQueries(['classwork-list']))
                                }
                              >
                                <Eye className="h-4 w-4" />
                                <span className="sr-only">Sichtbar</span>
                              </button>
                              <button
                                type="button"
                                title="Für Schüler ausblenden"
                                className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                onClick={() => {
                                  if (!window.confirm('Für Schüler ausblenden?')) return;
                                  updateClasswork(item.id, { status: 'closed' } as any)
                                    .then(() => toast.success('Unsichtbar'))
                                    .then(() => queryClient.invalidateQueries(['classwork-list']));
                                }}
                              >
                                <EyeOff className="h-4 w-4" />
                                <span className="sr-only">Unsichtbar</span>
                              </button>
                              <button
                                type="button"
                                title="Archivieren"
                                className="p-2 text-gray-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                                onClick={() => {
                                  if (!window.confirm('Archivieren?')) return;
                                  updateClasswork(item.id, { status: 'archived' } as any)
                                    .then(() => toast.success('Archiviert'))
                                    .then(() => queryClient.invalidateQueries(['classwork-list']));
                                }}
                              >
                                <Archive className="h-4 w-4" />
                                <span className="sr-only">Archivieren</span>
                              </button>
                              <button
                                type="button"
                                title="Löschen"
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                onClick={() => {
                                  if (!window.confirm('Wirklich löschen?')) return;
                                  deleteClasswork(item.id)
                                    .then(() => {
                                      toast.success('Gelöscht');
                                      if (listDetailId === item.id) setListDetailId(null);
                                      queryClient.invalidateQueries(['classwork-list']);
                                    })
                                    .catch(() => toast.error('Löschen fehlgeschlagen'));
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                                <span className="sr-only">Löschen</span>
                              </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {listDetailId ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-primary-200 bg-primary-50/60 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold text-gray-900">{String(listDetailItem?.title || 'Klassenarbeit')}</h3>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${detailStatusBadge.className}`}>
                          {detailStatusBadge.label}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-800 border border-indigo-200">
                          {returnModeLabel(listDetailReturnMode)}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {detailMetrics.graded > 0 ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary inline-flex items-center gap-1.5"
                          onClick={() => setShowResultsExport(true)}
                          title="Ergebnisse als PDF exportieren"
                        >
                          <Printer className="h-4 w-4" aria-hidden />
                          PDF-Ergebnisse
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          if (detailPrimaryAction.type === 'edit') {
                            openClassworkForEdit(Number(listDetailId));
                            return;
                          }
                          if (detailPrimaryAction.targetId) {
                            document.getElementById(detailPrimaryAction.targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }}
                      >
                        {detailPrimaryAction.label}
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-4 text-xs">
                    <div className="rounded-md border border-white/80 bg-white/90 px-2 py-1.5 text-gray-700">Teilnehmer: <strong>{detailMetrics.total}</strong></div>
                    <div className="rounded-md border border-white/80 bg-white/90 px-2 py-1.5 text-gray-700">Abgegeben: <strong>{detailMetrics.submitted}</strong></div>
                    <div className="rounded-md border border-white/80 bg-white/90 px-2 py-1.5 text-gray-700">Bewertet: <strong>{detailMetrics.graded}</strong></div>
                    <div className="rounded-md border border-white/80 bg-white/90 px-2 py-1.5 text-gray-700">Analysen aktiv: <strong>{detailMetrics.runningAnalysis}</strong></div>
                  </div>
                </div>
                <section id="cw-section-materials" className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900">Materialien & Referenzen</h4>
                <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-4 space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Korrekturraster & Musterlösung (nur Lehrkraft)</h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Diese Dateien sind für Schüler:innen nicht sichtbar. Hier sehen Sie den aktuellen Stand der Uploads.
                    </p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-white/80 bg-white p-3 shadow-sm">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-xs font-semibold text-gray-800 uppercase tracking-wide">Korrekturraster</span>
                        <span className="text-xs text-gray-500 tabular-nums">{listExpectationHorizonFiles.length} Datei(en)</span>
                      </div>
                      {listExpectationHorizonFiles.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">Keine Datei hochgeladen.</p>
                      ) : (
                        <ul className="space-y-2 text-sm">
                          {listExpectationHorizonFiles.map((m: any) => (
                            <li
                              key={m.id}
                              className="flex flex-wrap items-start justify-between gap-2 border border-gray-100 rounded-md px-2 py-1.5 bg-gray-50/80"
                            >
                              <div className="flex items-start gap-2 min-w-0">
                                <FileText className="h-4 w-4 text-primary-600 flex-shrink-0 mt-0.5" aria-hidden />
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-900 truncate">{m.title || m.original_name}</p>
                                  {m.mime_type ? (
                                    <p className="text-xs text-gray-500 truncate">{String(m.mime_type)}</p>
                                  ) : null}
                                </div>
                              </div>
                              <span className="flex gap-2 shrink-0">
                                <a
                                  className="text-primary-700 text-xs font-medium hover:underline"
                                  href={`/api/classwork/${listDetailId}/materials/${m.id}/download`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Download
                                </a>
                                <button
                                  type="button"
                                  className="text-red-600 text-xs font-medium hover:underline"
                                  onClick={() =>
                                    listDetailId &&
                                    deleteMaterialMutation.mutate({
                                      classworkId: Number(listDetailId),
                                      materialId: Number(m.id)
                                    })
                                  }
                                >
                                  Entfernen
                                </button>
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="rounded-lg border border-white/80 bg-white p-3 shadow-sm">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-xs font-semibold text-gray-800 uppercase tracking-wide">Musterlösung</span>
                        <span className="text-xs text-gray-500 tabular-nums">{listMusterloesungFiles.length} Datei(en)</span>
                      </div>
                      {listMusterloesungFiles.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">Keine Datei hochgeladen.</p>
                      ) : (
                        <ul className="space-y-2 text-sm">
                          {listMusterloesungFiles.map((m: any) => (
                            <li
                              key={m.id}
                              className="flex flex-wrap items-start justify-between gap-2 border border-gray-100 rounded-md px-2 py-1.5 bg-gray-50/80"
                            >
                              <div className="flex items-start gap-2 min-w-0">
                                <FileText className="h-4 w-4 text-primary-600 flex-shrink-0 mt-0.5" aria-hidden />
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-900 truncate">{m.title || m.original_name}</p>
                                  {m.mime_type ? (
                                    <p className="text-xs text-gray-500 truncate">{String(m.mime_type)}</p>
                                  ) : null}
                                </div>
                              </div>
                              <span className="flex gap-2 shrink-0">
                                <a
                                  className="text-primary-700 text-xs font-medium hover:underline"
                                  href={`/api/classwork/${listDetailId}/materials/${m.id}/download`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Download
                                </a>
                                <button
                                  type="button"
                                  className="text-red-600 text-xs font-medium hover:underline"
                                  onClick={() =>
                                    listDetailId &&
                                    deleteMaterialMutation.mutate({
                                      classworkId: Number(listDetailId),
                                      materialId: Number(m.id)
                                    })
                                  }
                                >
                                  Entfernen
                                </button>
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
                <details className="rounded-lg border border-blue-100 bg-blue-50/50 p-4 space-y-3">
                  <summary className="cursor-pointer text-sm font-semibold text-gray-900">Erweiterte Optionen: Versionierte Referenzpakete</summary>
                  <div className="mt-3 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-gray-600 mt-0.5">Basis für reproduzierbare KI-Korrektur und Debugging.</p>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={isBuildingReferenceVersion || isCancellingReferenceVersionBuild}
                        onClick={() => void runBuildReferenceVersion()}
                      >
                        {isBuildingReferenceVersion ? 'Erstelle …' : 'Neue Referenzversion bauen'}
                      </button>
                    </div>
                    {hasBuildingReferenceVersion ? (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          className="btn btn-xs btn-secondary !text-rose-700 !border-rose-200 !bg-rose-50 hover:!bg-rose-100"
                          disabled={isCancellingReferenceVersionBuild}
                          onClick={() => void runCancelReferenceVersionBuild()}
                        >
                          {isCancellingReferenceVersionBuild ? 'Breche ab …' : 'Build abbrechen'}
                        </button>
                      </div>
                    ) : null}
                    <div className="text-xs text-gray-700">
                      Aktiv: {activeReferenceVersion ? `v${activeReferenceVersion.version_no} (${activeReferenceVersion.status})` : 'keine'}
                    </div>
                    {!referenceVersions.length ? (
                      <p className="text-sm text-gray-500 italic">Noch keine Referenzversion vorhanden.</p>
                    ) : (
                      <ul className="space-y-2">
                        {referenceVersions.map((v) => {
                          const badge = referenceVersionStatusLabel(v.status);
                          let summary: any = v.build_summary_json;
                          if (typeof summary === 'string') {
                            try {
                              summary = JSON.parse(summary);
                            } catch {
                              summary = null;
                            }
                          }
                          const isActive = Boolean((v as any).is_active) || Number(activeReferenceVersion?.id) === Number(v.id);
                          return (
                            <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 bg-white border border-gray-200 rounded-md px-3 py-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">v{v.version_no}</span>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.className}`}>
                                    {badge.label}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600">
                                  Artefakte: {Number(summary?.artifactCount || 0)} · ok: {Number(summary?.completedCount || 0)} · fail:{' '}
                                  {Number(summary?.failedCount || 0)}
                                </p>
                              </div>
                              {isActive ? (
                                <span className="text-xs font-medium text-emerald-700">Aktiv</span>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-xs btn-secondary"
                                  disabled={isActivatingReferenceVersion === Number(v.id) || String(v.status) !== 'ready'}
                                  onClick={() => void runActivateReferenceVersion(Number(v.id))}
                                >
                                  {isActivatingReferenceVersion === Number(v.id) ? 'Aktiviere …' : 'Aktivieren'}
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </details>
                {activeReferenceVersion?.id && listDetailId ? (
                  <ClassworkRubricAcknowledgePanel
                    classworkId={Number(listDetailId)}
                    referenceSetVersionId={Number(activeReferenceVersion.id)}
                  />
                ) : null}
                </section>
                <section id="cw-section-returns" className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900">Rückläufe & Abgaben</h4>
                {listDetailReturnMode === 'paper_presence' ? (
                  <>
                    <div className="rounded-lg border border-gray-200 bg-white p-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">Rückläufe einsammeln</h4>
                      <p className="text-xs text-gray-600 mb-3">
                        Nach der Durchführung hier Sammel-PDF(s) oder einzelne Schülerbeiträge hochladen.
                      </p>
                      <ClassworkPenPaperImport
                        classworkId={listDetailId != null ? Number(listDetailId) : null}
                        participantOptions={listParticipantOptions}
                        loading={
                          penpaperMutation.isLoading ||
                          penpaperBulkStartMutation.isLoading ||
                          penpaperBulkSegmentsMutation.isLoading ||
                          penpaperBulkFinalizeMutation.isLoading
                        }
                        onImport={async (payload) => {
                          const id = listDetailId;
                          if (!id) return;
                          const data = await penpaperMutation.mutateAsync({ classworkId: Number(id), ...payload });
                          return data;
                        }}
                        onUploadScan={async ({ student_id, file }) => {
                          try {
                            const id = listDetailId;
                            if (!id) return;
                            await uploadClassworkTeacherScan(Number(id), student_id, file);
                            toast.success('Lehrer-Datei gespeichert');
                            queryClient.invalidateQueries(['classwork-detail', id]);
                          } catch (error: any) {
                            toast.error(error?.response?.data?.error || 'Datei-Upload fehlgeschlagen');
                          }
                        }}
                        onUploadScanBulk={async ({ files }) => {
                          try {
                            const id = listDetailId;
                            if (!id || !files.length) return;
                            const data = await uploadClassworkTeacherScansBulk(Number(id), files);
                            const uploaded = Number(data?.uploaded_count || 0);
                            const unmatched = Number(data?.unmatched_count || 0);
                            const failed = Number(data?.failed_count || 0);
                            toast.success(`Bulk-Upload: ${uploaded} zugeordnet${unmatched ? `, ${unmatched} offen` : ''}${failed ? `, ${failed} Fehler` : ''}`);
                            queryClient.invalidateQueries(['classwork-detail', id]);
                          } catch (error: any) {
                            toast.error(error?.response?.data?.error || 'Bulk-Upload fehlgeschlagen');
                          }
                        }}
                        onGeneratePrintSheets={async ({ layout }) => {
                          try {
                            const id = listDetailId;
                            if (!id) return;
                            const blob = await downloadClassworkPrintSheets(Number(id), { layout });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `klassenarbeitspapier-${Number(id)}-${layout}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(url);
                            toast.success('Klassenarbeitspapier bereit');
                          } catch (error: any) {
                            toast.error(error?.response?.data?.error || 'PDF-Generierung fehlgeschlagen');
                          }
                        }}
                        onBulkImportStart={async ({ strategy, file }) => {
                          const id = listDetailId;
                          if (!id) return;
                          const data = await penpaperBulkStartMutation.mutateAsync({ classworkId: Number(id), strategy, file });
                          toast.success(
                            data?.async ? 'Sammel-PDF wird im Hintergrund analysiert …' : 'Sammel-PDF Import gestartet'
                          );
                          return data;
                        }}
                        onBulkImportSaveSegments={async ({ runId, segments }) => {
                          const id = listDetailId;
                          if (!id) return;
                          const data = await penpaperBulkSegmentsMutation.mutateAsync({ classworkId: Number(id), runId, segments });
                          return data;
                        }}
                        onBulkImportFinalize={async ({ runId }) => {
                          const id = listDetailId;
                          if (!id) return;
                          await penpaperBulkFinalizeMutation.mutateAsync({ classworkId: Number(id), runId });
                        }}
                      />
                    </div>
                    {renderBatchActions()}
                    <ClassworkSubmissionBoard
                      assignments={assignments}
                      classworkId={Number(listDetailId)}
                      latestSubmissionByAssignmentId={latestSubmissionByAssignmentId}
                      archivedSubmissionsByAssignmentId={archivedSubmissionsByAssignmentId}
                      gradeRowByAssignmentId={gradeRowByAssignmentId}
                      latestCorrectionByAssignmentId={latestCorrectionByAssignmentId}
                      correctionRunningByAssignmentId={runningCorrectionsByAssignmentId}
                      correctionPipelineLabelByAssignmentId={mergedCorrectionPipelineLabelByAssignmentId}
                      onlyofficeWarningsByAssignmentId={onlyofficeWarningsByAssignmentId}
                      onRetryOnlyoffice={(assignmentId) => {
                        const id = listDetailId;
                        if (!id) return;
                        resetOnlyofficeRetry(Number(id), assignmentId)
                          .then(() => {
                            toast.success('Neuversuch freigegeben');
                            queryClient.invalidateQueries(['classwork-detail', id]);
                          })
                          .catch((error: any) => {
                            toast.error(error?.response?.data?.error || 'Neuversuch konnte nicht freigegeben werden');
                          });
                      }}
                      onQueueCorrection={(assignmentId) => {
                        void runCorrectionWithJobPoll(assignmentId);
                      }}
                      onViewCorrectionResult={(assignmentId) => {
                        setSelectedAssignmentId(assignmentId);
                      }}
                      onOpenPipelineReview={(assignmentId) => {
                        setPipelineReviewAssignmentId(assignmentId);
                      }}
                      onArchiveSubmission={handleArchiveSubmission}
                      onRestoreSubmission={handleRestoreSubmission}
                      onPublish={(assignmentId, options) => {
                        const id = listDetailId;
                        if (!id) return;
                        if (options?.revoke) {
                          const ok = window.confirm('Veröffentlichte Note wirklich widerrufen? Der Status springt zurück auf "Bewertet".');
                          if (!ok) return;
                        }
                        publishGrade(Number(id), assignmentId)
                          .then((data) => {
                            const published = Boolean(data?.published);
                            if (published) {
                              const competencyCount = Math.max(0, Number(data?.evidenceSync?.created || 0));
                              toast.success(`Noten veröffentlicht · ${competencyCount} Kompetenzen vergeben.`);
                            } else {
                              const removedCompetencyCount = Math.max(0, Number(data?.evidenceSync?.removed || 0));
                              toast.success(`Veröffentlichung widerrufen · ${removedCompetencyCount} Kompetenzen zurückgezogen.`);
                            }
                            queryClient.invalidateQueries(['classwork-detail', id]);
                          })
                          .catch((error: any) => {
                            if (error?.message === 'assignment_id_missing') {
                              toast.error('Interner Fehler: assignment_id fehlt für diese Zeile');
                              return;
                            }
                            toast.error(error?.response?.data?.error || 'Statuswechsel fehlgeschlagen');
                          });
                      }}
                    />
                  </>
                ) : listDetailReturnMode !== 'results_import_only' ? (
                  <>
                    {renderBatchActions()}
                    <ClassworkSubmissionBoard
                      assignments={assignments}
                      classworkId={Number(listDetailId)}
                      latestSubmissionByAssignmentId={latestSubmissionByAssignmentId}
                      archivedSubmissionsByAssignmentId={archivedSubmissionsByAssignmentId}
                      gradeRowByAssignmentId={gradeRowByAssignmentId}
                      latestCorrectionByAssignmentId={latestCorrectionByAssignmentId}
                      correctionRunningByAssignmentId={runningCorrectionsByAssignmentId}
                      correctionPipelineLabelByAssignmentId={mergedCorrectionPipelineLabelByAssignmentId}
                      onlyofficeWarningsByAssignmentId={onlyofficeWarningsByAssignmentId}
                      onRetryOnlyoffice={(assignmentId) => {
                        const id = listDetailId;
                        if (!id) return;
                        resetOnlyofficeRetry(Number(id), assignmentId)
                          .then(() => {
                            toast.success('Neuversuch freigegeben');
                            queryClient.invalidateQueries(['classwork-detail', id]);
                          })
                          .catch((error: any) => {
                            toast.error(error?.response?.data?.error || 'Neuversuch konnte nicht freigegeben werden');
                          });
                      }}
                      onQueueCorrection={(assignmentId) => {
                        void runCorrectionWithJobPoll(assignmentId);
                      }}
                      onViewCorrectionResult={(assignmentId) => {
                        setSelectedAssignmentId(assignmentId);
                      }}
                      onOpenPipelineReview={(assignmentId) => {
                        setPipelineReviewAssignmentId(assignmentId);
                      }}
                      onArchiveSubmission={handleArchiveSubmission}
                      onRestoreSubmission={handleRestoreSubmission}
                      onPublish={(assignmentId, options) => {
                        const id = listDetailId;
                        if (!id) return;
                        if (options?.revoke) {
                          const ok = window.confirm('Veröffentlichte Note wirklich widerrufen? Der Status springt zurück auf "Bewertet".');
                          if (!ok) return;
                        }
                        publishGrade(Number(id), assignmentId)
                          .then((data) => {
                            const published = Boolean(data?.published);
                            if (published) {
                              const competencyCount = Math.max(0, Number(data?.evidenceSync?.created || 0));
                              toast.success(`Noten veröffentlicht · ${competencyCount} Kompetenzen vergeben.`);
                            } else {
                              const removedCompetencyCount = Math.max(0, Number(data?.evidenceSync?.removed || 0));
                              toast.success(`Veröffentlichung widerrufen · ${removedCompetencyCount} Kompetenzen zurückgezogen.`);
                            }
                            queryClient.invalidateQueries(['classwork-detail', id]);
                          })
                          .catch((error: any) => {
                            if (error?.message === 'assignment_id_missing') {
                              toast.error('Interner Fehler: assignment_id fehlt für diese Zeile');
                              return;
                            }
                            toast.error(error?.response?.data?.error || 'Statuswechsel fehlgeschlagen');
                          });
                      }}
                    />
                  </>
                ) : (
                  <ClassworkPenPaperImport
                    classworkId={listDetailId != null ? Number(listDetailId) : null}
                    participantOptions={listParticipantOptions}
                    loading={penpaperMutation.isLoading}
                    onImport={async (payload) => {
                      const id = listDetailId;
                      if (!id) return;
                      const data = await penpaperMutation.mutateAsync({ classworkId: Number(id), ...payload });
                      return data;
                    }}
                  />
                )}
                </section>
                <section id="cw-section-correction" className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900">Korrektur & Veröffentlichung</h4>
                  {!selectedAssignmentId ? (
                    <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
                      Wähle im Bereich „Rückläufe & Abgaben“ eine Abgabe über „KI-Ergebnis“ aus, um Details zu prüfen und final zu bewerten.
                    </div>
                  ) : null}
                {listDetailId && diagnosticsQuery.data ? (
                  <details className="bg-white rounded-lg border border-gray-200 p-4 mt-4">
                    <summary className="cursor-pointer text-sm font-semibold text-gray-800">Admin-Diagnose: Vision-Pipeline</summary>
                    <div className="mt-3 text-sm text-gray-700 space-y-3">
                      <div className="flex justify-end">
                        <button
                          type="button"
                          className="btn-secondary text-xs"
                          onClick={() => {
                            void diagnosticsQuery.refetch();
                            void pipelineDecisionsQuery.refetch();
                            void pipelineMetricsQuery.refetch();
                            void pipelineReviewReasonsQuery.refetch();
                          }}
                          disabled={
                            diagnosticsQuery.isFetching ||
                            pipelineDecisionsQuery.isFetching ||
                            pipelineMetricsQuery.isFetching ||
                            pipelineReviewReasonsQuery.isFetching
                          }
                        >
                          {diagnosticsQuery.isFetching ||
                          pipelineDecisionsQuery.isFetching ||
                          pipelineMetricsQuery.isFetching ||
                          pipelineReviewReasonsQuery.isFetching
                            ? 'Aktualisiere …'
                            : 'Aktualisieren'}
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="rounded border border-indigo-200 bg-indigo-50 p-2">
                          <div className="text-[11px] uppercase tracking-wide text-indigo-700">Auto-Grade</div>
                          <div className="text-lg font-semibold text-indigo-900">{autoGradeCount}</div>
                        </div>
                        <div className="rounded border border-rose-200 bg-rose-50 p-2">
                          <div className="text-[11px] uppercase tracking-wide text-rose-700">Human Review</div>
                          <div className="text-lg font-semibold text-rose-900">{humanReviewCount}</div>
                        </div>
                        <div className="rounded border border-amber-200 bg-amber-50 p-2">
                          <div className="text-[11px] uppercase tracking-wide text-amber-700">Repair-Läufe</div>
                          <div className="text-lg font-semibold text-amber-900">{repairCount}</div>
                        </div>
                        <div className="rounded border border-slate-200 bg-slate-50 p-2">
                          <div className="text-[11px] uppercase tracking-wide text-slate-700">Scorer Divergence</div>
                          <div className="text-lg font-semibold text-slate-900">
                            {scorerDivergenceMetric?.metric_value != null
                              ? `${Math.round(Number(scorerDivergenceMetric.metric_value) * 100) / 100}`
                              : 'n/a'}
                          </div>
                        </div>
                      </div>
                      {latestPolicyDecision ? (
                        <div className="rounded border border-slate-200 bg-slate-50 p-2 text-xs">
                          <div className="font-semibold text-slate-800">Letzte Policy-Entscheidung</div>
                          <div className="mt-1 text-slate-700">
                            {String(latestPolicyDecision.decision_name || 'unknown')} ·{' '}
                            {String(latestPolicyDecision.reason_code || 'OK')}
                            {latestPolicyDecision.reasonMeta?.label
                              ? ` (${latestPolicyDecision.reasonMeta.label})`
                              : ''}
                            {latestPolicyDecision.confidence != null
                              ? ` · Confidence ${Math.round(Number(latestPolicyDecision.confidence) * 100)}%`
                              : ''}
                          </div>
                          {latestPolicyDecision.reasonMeta?.teacherHint ? (
                            <div className="mt-1 text-slate-600">{latestPolicyDecision.reasonMeta.teacherHint}</div>
                          ) : null}
                        </div>
                      ) : null}
                      {pipelineReviewReasonItems.length > 0 ? (
                        <div>
                          <div className="font-medium text-gray-800 mb-1">Top-Review-Ursachen</div>
                          <ul className="list-disc ml-5 text-gray-700">
                            {pipelineReviewReasonItems.slice(0, 6).map((r: any, i: number) => (
                              <li key={`reason-${i}`}>
                                {String(r.reasonCode || 'REVIEW_REQUIRED')}: {Number(r.count || 0)}
                                {r?.explanation?.label ? ` · ${String(r.explanation.label)}` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      <div>
                        <div className="font-medium text-gray-800 mb-1">Submission-Status</div>
                        <ul className="list-disc ml-5">
                          {(diagnosticsQuery.data.submissionStatus || []).map((r: any, i: number) => (
                            <li key={`sub-${i}`}>{String(r.analysis_status || 'unknown')}: {Number(r.cnt || 0)}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div className="font-medium text-gray-800 mb-1">Referenzartefakt-Status</div>
                        <ul className="list-disc ml-5">
                          {(diagnosticsQuery.data.referenceStatus || []).map((r: any, i: number) => (
                            <li key={`ref-${i}`}>{String(r.analysis_status || 'unknown')}: {Number(r.cnt || 0)}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div className="font-medium text-gray-800 mb-1">Coverage nach Quelle</div>
                        <ul className="list-disc ml-5">
                          {(diagnosticsQuery.data.coverageBySource || []).map((r: any, i: number) => (
                            <li key={`cov-${i}`}>
                              {String(r.source_type || 'unknown')} · {String(r.analysis_status || 'unknown')}: {Number(r.cnt || 0)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {(diagnosticsQuery.data.recentErrors || []).length > 0 ? (
                        <div>
                          <div className="font-medium text-red-700 mb-1">Letzte Fehler</div>
                          <ul className="list-disc ml-5 text-red-700">
                            {diagnosticsQuery.data.recentErrors.map((e: any, i: number) => (
                              <li key={`err-${i}`}>
                                [{String(e.owner_type || '-')}/{String(e.owner_id || '-')}] {String(e.source_type || '-')}: {String(e.error_message || 'unknown')}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <p className="text-emerald-700">Keine aktuellen Analysefehler.</p>
                      )}
                      {pipelineDecisionItems.length > 0 ? (
                        <div>
                          <div className="font-medium text-gray-800 mb-1">Letzte Pipeline-Entscheidungen</div>
                          <ul className="list-disc ml-5">
                            {pipelineDecisionItems.slice(0, 8).map((d: any, i: number) => (
                              <li key={`decision-${i}`}>
                                {String(d.stage_name || 'stage')} · {String(d.decision_name || 'decision')} ·{' '}
                                {String(d.reason_code || 'OK')}
                                {d?.reasonMeta?.label ? ` (${String(d.reasonMeta.label)})` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  </details>
                ) : null}
                {selectedAssignmentId && listDetailId ? (
                  <div
                    className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
                    onClick={() => setSelectedAssignmentId(null)}
                  >
                    <div
                      className="w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-xl bg-white shadow-2xl border border-gray-200 p-4 sm:p-6"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">KI-Analyse & Bewertung</h3>
                          {selectedCorrectionSubtitle ? (
                            <p className="text-xs text-gray-600 mt-0.5">{selectedCorrectionSubtitle}</p>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className="btn-secondary text-xs"
                          onClick={() => setSelectedAssignmentId(null)}
                        >
                          Schließen
                        </button>
                      </div>
                      <div className="space-y-4">
                        <ClassworkLlmCorrectionView
                          classworkId={Number(listDetailId)}
                          assignmentId={selectedAssignmentId}
                          subtitle={selectedCorrectionSubtitle}
                          onRerunCorrection={(assignmentId) => runCorrectionWithJobPoll(assignmentId, { forceRerun: true })}
                          onRestoreCorrection={(assignmentId, correctionId) => handleRestoreCorrection(assignmentId, correctionId)}
                          persistedGradeRow={
                            selectedAssignmentId ? gradeRowByAssignmentId[selectedAssignmentId] ?? null : null
                          }
                        />
                        <ClassworkCorrectionReview
                          classworkSubjectId={
                            listDetailQuery.data?.item?.subject_id != null
                              ? Number(listDetailQuery.data.item.subject_id)
                              : null
                          }
                          boundGradeSchemaId={
                            listDetailQuery.data?.item?.grade_schema_id != null
                              ? Number(listDetailQuery.data.item.grade_schema_id)
                              : null
                          }
                          onFinalize={(payload) => {
                            const id = listDetailId;
                            if (!id) return Promise.resolve();
                            return finalizeGrade(Number(id), selectedAssignmentId, payload)
                              .then(() => {
                                toast.success('Bewertung gespeichert');
                                queryClient.invalidateQueries(['classwork-detail', id]);
                              })
                              .catch(() => toast.error('Speichern fehlgeschlagen'));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                  <p>
                    Rücklaufmodus: <strong>{returnModeLabel(listDetailReturnMode)}</strong>.
                  </p>
                  <p className="mt-1 text-xs">
                    Analysen und Korrekturläufe laufen im Hintergrund. Du kannst weiterarbeiten und den Status live im Board verfolgen.
                  </p>
                </div>
                </section>
                {listDetailId ? (
                  <ClassworkResultsExportModal
                    open={showResultsExport}
                    classworkId={Number(listDetailId)}
                    classworkTitle={listDetailQuery.data?.item?.title || null}
                    assignments={assignments}
                    gradeRowByAssignmentId={gradeRowByAssignmentId}
                    onClose={() => setShowResultsExport(false)}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between xl:gap-4">
              <div className="grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {steps.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    disabled={s.id > 1 && !wizardClassworkId}
                    onClick={() => setWizardStep(s.id)}
                    className={`min-w-0 text-left px-3 py-2 rounded-lg text-xs border transition-colors ${
                      wizardStep === s.id ? 'border-primary-500 bg-primary-50 text-primary-900' : 'border-gray-200 bg-white text-gray-700'
                    } ${s.id > 1 && !wizardClassworkId ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                          wizardStep === s.id ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {s.id}
                      </span>
                      <span className="min-w-0 font-medium leading-snug break-words">{s.title}</span>
                    </div>
                    <p className="mt-1 pl-7 text-[11px] text-gray-500 leading-snug break-words">{s.subtitle}</p>
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-secondary shrink-0 self-end font-medium xl:self-start"
                onClick={resetWizard}
              >
                Wizard zurücksetzen
              </button>
            </div>

            <div className="rounded-xl border border-gray-300 bg-gradient-to-b from-slate-50/90 to-white shadow-md p-6 sm:p-8 min-h-[320px]">
              {wizardStep === 1 && (
                <div className="space-y-5 max-w-xl">
                  <h2 className="text-xl font-semibold text-gray-900">Klassenarbeit formal anlegen</h2>
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-1.5">Titel *</label>
                    <input className="input-field" value={wTitle} onChange={(e) => setWTitle(e.target.value)} placeholder="Titel" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-800 mb-1.5">Fach</label>
                      <select className="input-field" value={wSubjectId} onChange={(e) => setWSubjectId(e.target.value)}>
                        <option value="">—</option>
                        {(teachingMetaQuery.data?.subjects || []).map((s) => (
                          <option key={s.id} value={String(s.id)}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-800 mb-1.5">Curriculum</label>
                      <select className="input-field" value={wCurriculumId} onChange={(e) => setWCurriculumId(e.target.value)}>
                        <option value="">—</option>
                        {(teachingMetaQuery.data?.curricula || []).map((c) => (
                          <option key={c.id} value={String(c.id)}>
                            {c.name}
                            {c.subject ? ` (${c.subject})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    Teilnehmer werden erst im letzten Schritt zugewiesen, damit die Arbeit zunächst inhaltlich vollständig vorbereitet werden kann.
                  </p>
                  <div className={WIZARD_ACTION_ROW}>
                    <button
                      type="button"
                      className={WIZARD_BTN_PRIMARY}
                      onClick={() => void handleStep1Next()}
                    >
                      Weiter
                    </button>
                    <button type="button" className={WIZARD_BTN_DRAFT} onClick={() => void saveDraft()} disabled={!wizardClassworkId}>
                      Entwurf speichern
                    </button>
                  </div>
                </div>
              )}

              {wizardStep === 2 && wizardClassworkId && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-gray-900">Aufgabenstellung & Material</h2>
                  <textarea
                    className="input-field min-h-[120px]"
                    placeholder="Aufgabenstellung / Beschreibung"
                    value={wDescription}
                    onChange={(e) => setWDescription(e.target.value)}
                  />
                  <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-4 space-y-3">
                    <div className="grid sm:grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-medium text-gray-700">Materialtyp</label>
                        <select className="input-field" value={materialType} onChange={(e) => setMaterialType(e.target.value as any)}>
                          <option value="aufgabenstellung">Aufgabenstellung</option>
                          <option value="rueckgabe">Rückgabedokument</option>
                          <option value="zusaetzlich">Zusätzliche Datei</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700">Titel (optional)</label>
                        <input className="input-field" value={materialTitle} onChange={(e) => setMaterialTitle(e.target.value)} />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="text-xs font-medium text-gray-700">Datei</label>
                        <input
                          id="classwork-material-upload-input"
                          type="file"
                          className="input-field"
                          onChange={(e) => setMaterialFile(e.target.files?.[0] || null)}
                        />
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={materialRequired} onChange={(e) => setMaterialRequired(e.target.checked)} />
                        Pflicht
                      </label>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary font-semibold shadow-sm"
                      disabled={!materialFile || uploadMaterialMutation.isLoading}
                      onClick={() => uploadMaterialMutation.mutate()}
                    >
                      Hochladen
                    </button>
                    <div className="border-t border-blue-200 pt-3">
                      <p className="text-xs font-semibold mb-2">Internetlink</p>
                      <input
                        className="input-field mb-2"
                        type="url"
                        placeholder="https://…"
                        value={materialLinkUrl}
                        onChange={(e) => setMaterialLinkUrl(e.target.value)}
                      />
                      <input
                        className="input-field mb-2"
                        placeholder="Linktitel"
                        value={materialLinkTitle}
                        onChange={(e) => setMaterialLinkTitle(e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline font-medium border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                        disabled={!materialLinkUrl.trim() || createMaterialLinkMutation.isLoading}
                        onClick={() => createMaterialLinkMutation.mutate()}
                      >
                        Link veröffentlichen
                      </button>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {wizardMaterials.map((m: any) => (
                      <li key={m.id} className="flex flex-wrap justify-between gap-2 text-sm border rounded-lg px-3 py-2">
                        <span>
                          {m.title} · {materialTypeLabel(m.material_type)}
                        </span>
                        <span className="flex gap-2">
                          {String(m.file_source || 'upload') === 'external' && m.external_url ? (
                            <a className="text-primary-700" href={m.external_url} target="_blank" rel="noreferrer">
                              Link
                            </a>
                          ) : (
                            <a
                              className="text-primary-700"
                              href={`/api/classwork/${wizardClassworkId}/materials/${m.id}/download`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Download
                            </a>
                          )}
                          {isOfficeMaterial(m.mime_type, m.original_name) ? (
                            <button
                              type="button"
                              className="text-primary-700"
                              onClick={async () => {
                                try {
                                  const session = await openClassworkMaterialOnlyofficeSession(Number(wizardClassworkId), Number(m.id));
                                  setMaterialSession(session);
                                  setMaterialEditorTargetId(Number(m.id));
                                } catch (error: any) {
                                  toast.error(error?.response?.data?.error || 'OnlyOffice konnte nicht geöffnet werden');
                                }
                              }}
                            >
                              OnlyOffice
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="text-red-600"
                            onClick={() =>
                              wizardClassworkId &&
                              deleteMaterialMutation.mutate({
                                classworkId: Number(wizardClassworkId),
                                materialId: Number(m.id)
                              })
                            }
                          >
                            Entfernen
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className={WIZARD_ACTION_ROW}>
                    <button type="button" className={WIZARD_BTN_BACK} onClick={() => setWizardStep(1)}>
                      Zurück
                    </button>
                    <button
                      type="button"
                      className={WIZARD_BTN_PRIMARY}
                      onClick={async () => {
                        const saved = await patchWizard({ description: wDescription.trim() || null });
                        if (!saved) return;
                        setWizardStep(3);
                      }}
                    >
                      Weiter
                    </button>
                    <button type="button" className={WIZARD_BTN_DRAFT} onClick={() => void saveDraft()}>
                      Entwurf speichern
                    </button>
                  </div>
                </div>
              )}

              {wizardStep === 2 && !wizardClassworkId && (
                <p className="text-sm text-amber-800">Bitte zuerst Schritt 1 abschließen.</p>
              )}

              {wizardStep === 4 && wizardClassworkId && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-gray-900">Durchführung & Rücklaufmodus</h2>
                  <p className="text-sm text-gray-600">
                    Lege fest, wie die Rückläufe dieser Klassenarbeit erfolgen. LLM-/Vision-Analysen laufen später immer im Hintergrund.
                  </p>
                  <div className="max-w-xl space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-800 mb-1">Rücklaufmodus</p>
                    <p className="text-xs text-gray-500 mb-3">
                      Ein Modus steuert den gesamten Ablauf in der Durchführungsphase.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className={`flex-1 min-w-[140px] px-3 py-2.5 text-sm font-medium rounded-lg border-2 transition-colors ${
                          wizardReturnMode === 'digital_system' && !isWizardFileUploadMode
                            ? 'border-primary-600 bg-primary-50 text-gray-900'
                            : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300'
                        }`}
                        disabled={!wizardClassworkId || updateReturnModeMutation.isLoading}
                        onClick={() => updateReturnModeMutation.mutate({ mode: 'digital_system', digitalMode: digitalSubmissionChoice })}
                      >
                        Digital im System
                      </button>
                      <button
                        type="button"
                        className={`flex-1 min-w-[140px] px-3 py-2.5 text-sm font-medium rounded-lg border-2 transition-colors ${
                          isWizardFileUploadMode
                            ? 'border-primary-600 bg-primary-50 text-gray-900'
                            : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300'
                        }`}
                        disabled={!wizardClassworkId || updateReturnModeMutation.isLoading}
                        onClick={() => {
                          setDigitalSubmissionChoice('upload');
                          updateReturnModeMutation.mutate({ mode: 'digital_system', digitalMode: 'upload' });
                        }}
                      >
                        Datei Upload
                      </button>
                      <button
                        type="button"
                        className={`flex-1 min-w-[140px] px-3 py-2.5 text-sm font-medium rounded-lg border-2 transition-colors ${
                          wizardReturnMode === 'paper_presence'
                            ? 'border-primary-600 bg-primary-50 text-gray-900'
                            : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300'
                        }`}
                        disabled={!wizardClassworkId || updateReturnModeMutation.isLoading}
                        onClick={() => updateReturnModeMutation.mutate({ mode: 'paper_presence', digitalMode: digitalSubmissionChoice })}
                      >
                        Papier in Präsenz
                      </button>
                      <button
                        type="button"
                        className={`flex-1 min-w-[140px] px-3 py-2.5 text-sm font-medium rounded-lg border-2 transition-colors ${
                          wizardReturnMode === 'paper_photo_upload'
                            ? 'border-primary-600 bg-primary-50 text-gray-900'
                            : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300'
                        }`}
                        disabled={!wizardClassworkId || updateReturnModeMutation.isLoading}
                        onClick={() => updateReturnModeMutation.mutate({ mode: 'paper_photo_upload', digitalMode: digitalSubmissionChoice })}
                      >
                        Abfotografieren
                      </button>
                      <button
                        type="button"
                        className={`flex-1 min-w-[180px] px-3 py-2.5 text-sm font-medium rounded-lg border-2 transition-colors ${
                          wizardReturnMode === 'results_import_only'
                            ? 'border-primary-600 bg-primary-50 text-gray-900'
                            : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300'
                        }`}
                        disabled={!wizardClassworkId || updateReturnModeMutation.isLoading}
                        onClick={() => updateReturnModeMutation.mutate({ mode: 'results_import_only', digitalMode: digitalSubmissionChoice })}
                      >
                        Nur Ergebnisse importieren
                      </button>
                    </div>
                    {wizardReturnMode === 'digital_system' ? (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-700 mb-1">Digitale Abgabeart</p>
                        <div className="flex flex-wrap gap-2">
                          {(['onlyoffice', 'tiptap'] as const).map((mode) => (
                            <button
                              key={mode}
                              type="button"
                              className={`px-3 py-1.5 text-xs rounded-lg border ${
                                digitalSubmissionChoice === mode
                                  ? 'border-primary-600 bg-primary-50 text-primary-900'
                                  : 'border-gray-300 bg-white text-gray-700'
                              }`}
                              onClick={() => {
                                setDigitalSubmissionChoice(mode);
                                updateReturnModeMutation.mutate({ mode: 'digital_system', digitalMode: mode });
                              }}
                            >
                              {mode === 'onlyoffice' ? 'OnlyOffice' : 'TipTap'}
                            </button>
                          ))}
                        </div>
                        {isWizardFileUploadMode ? (
                          <div className="mt-3 max-w-md">
                            <label className="text-xs font-medium text-gray-700 mb-1">Erlaubte Dateitypen für Schüler-Upload</label>
                            <select
                              className="input-field"
                              value={wStudentUploadMode}
                              onChange={(e) => {
                                const mode = e.target.value as StudentUploadMode;
                                setWStudentUploadMode(mode);
                                void patchWizard({ student_upload_mode: mode });
                              }}
                            >
                              <option value="all_common">Alle gängigen Formate (Bild, PDF, DOCX, PPT, XLS, ...)</option>
                              <option value="images_pdf">Nur Bilder und PDF</option>
                              <option value="images_only">Nur Bilder (z. B. Handyfotos)</option>
                              <option value="pdf_only">Nur PDF</option>
                            </select>
                            <p className="mt-2 text-[11px] text-gray-500">Kategorien: Bilder, PDF und Office-Dokumente (je nach Auswahl).</p>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {digitalSubmissionMode === 'ambiguous' ? (
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2">
                        Es sind mehrere digitale Optionen gleichzeitig aktiv (Altbestand). Bitte oben einen Modus auswählen.
                      </p>
                    ) : null}
                    {digitalSubmissionMode === 'none' && wizardReturnMode === 'digital_system' ? (
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2">
                        Keine digitale Abgabeart gewählt. Bitte OnlyOffice, TipTap oder Datei-Upload wählen.
                      </p>
                    ) : null}
                  </div>
                    <div className="border-t border-gray-200 pt-3">
                      <button
                        type="button"
                        className="text-xs font-medium text-gray-700 underline"
                        onClick={() => setShowWizardAdvancedSchedule((prev) => !prev)}
                      >
                        {showWizardAdvancedSchedule ? 'Erweiterte Durchführung ausblenden' : 'Erweiterte Durchführung anzeigen'}
                      </button>
                      {showWizardAdvancedSchedule ? (
                        <div className="mt-3 space-y-3">
                          <div>
                            <label className="text-xs font-medium text-gray-700 mb-1">Geschätzte Bearbeitungszeit (Min)</label>
                            <input
                              className="input-field"
                              type="number"
                              min={1}
                              max={600}
                              value={wEstimatedMinutes}
                              onChange={(e) => setWEstimatedMinutes(e.target.value)}
                            />
                          </div>
                          <div className="grid sm:grid-cols-1 gap-3">
                            <div>
                              <label className="text-xs font-medium text-gray-700 mb-1">Start (optional)</label>
                              <input
                                className="input-field"
                                type="datetime-local"
                                value={wStartsAt}
                                onChange={(e) => setWStartsAt(e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-700 mb-1">Abgabe bis (optional)</label>
                              <input className="input-field" type="datetime-local" value={wDueAt} onChange={(e) => setWDueAt(e.target.value)} />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-700 mb-1">Späteste Abgabe (optional)</label>
                              <input
                                className="input-field"
                                type="datetime-local"
                                value={wLatestSubmitAt}
                                onChange={(e) => setWLatestSubmitAt(e.target.value)}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-700 mb-1">Max. Versuche</label>
                            <input
                              className="input-field max-w-xs"
                              type="number"
                              min={1}
                              max={20}
                              value={wMaxAttempts}
                              onChange={(e) => setWMaxAttempts(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-700 mb-1">Prüfungsmodus (SEB)</label>
                            <select className="input-field max-w-md" value={wSebMode} onChange={(e) => setWSebMode(e.target.value as any)}>
                              <option value="seb_optional">SEB optional</option>
                              <option value="seb_required">SEB erforderlich</option>
                            </select>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {wizardReturnMode === 'paper_presence' || wizardReturnMode === 'results_import_only' ? (
                    <ClassworkPenPaperImport
                      classworkId={wizardClassworkId != null ? Number(wizardClassworkId) : null}
                      participantOptions={wizardParticipantOptions}
                      loading={penpaperMutation.isLoading || penpaperBulkStartMutation.isLoading || penpaperBulkSegmentsMutation.isLoading || penpaperBulkFinalizeMutation.isLoading}
                      onImport={async (payload) => {
                        const id = wizardClassworkId;
                        if (!id) return;
                        const data = await penpaperMutation.mutateAsync({ classworkId: Number(id), ...payload });
                        return data;
                      }}
                      onUploadScan={wizardReturnMode === 'paper_presence' ? async ({ student_id, file }) => {
                        try {
                          const id = wizardClassworkId;
                          if (!id) return;
                          await uploadClassworkTeacherScan(Number(id), student_id, file);
                          toast.success('Lehrer-Datei gespeichert');
                          queryClient.invalidateQueries(['classwork-detail', id]);
                        } catch (error: any) {
                          toast.error(error?.response?.data?.error || 'Datei-Upload fehlgeschlagen');
                        }
                      } : undefined}
                      onUploadScanBulk={wizardReturnMode === 'paper_presence' ? async ({ files }) => {
                        try {
                          const id = wizardClassworkId;
                          if (!id || !files.length) return;
                          const data = await uploadClassworkTeacherScansBulk(Number(id), files);
                          const uploaded = Number(data?.uploaded_count || 0);
                          const unmatched = Number(data?.unmatched_count || 0);
                          const failed = Number(data?.failed_count || 0);
                          toast.success(`Bulk-Upload: ${uploaded} zugeordnet${unmatched ? `, ${unmatched} offen` : ''}${failed ? `, ${failed} Fehler` : ''}`);
                          queryClient.invalidateQueries(['classwork-detail', id]);
                        } catch (error: any) {
                          toast.error(error?.response?.data?.error || 'Bulk-Upload fehlgeschlagen');
                        }
                      } : undefined}
                      onGeneratePrintSheets={wizardReturnMode === 'paper_presence' ? async ({ layout }) => {
                        try {
                          const id = wizardClassworkId;
                          if (!id) return;
                          const blob = await downloadClassworkPrintSheets(Number(id), { layout });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `klassenarbeitspapier-${Number(id)}-${layout}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success('Klassenarbeitspapier bereit');
                        } catch (error: any) {
                          toast.error(error?.response?.data?.error || 'PDF-Generierung fehlgeschlagen');
                        }
                      } : undefined}
                      onBulkImportStart={wizardReturnMode === 'paper_presence' ? async ({ strategy, file }) => {
                        const id = wizardClassworkId;
                        if (!id) return;
                        const data = await penpaperBulkStartMutation.mutateAsync({ classworkId: Number(id), strategy, file });
                        toast.success(
                          data?.async ? 'Sammel-PDF wird im Hintergrund analysiert …' : 'Sammel-PDF Import gestartet'
                        );
                        return data;
                      } : undefined}
                      onBulkImportSaveSegments={wizardReturnMode === 'paper_presence' ? async ({ runId, segments }) => {
                        const id = wizardClassworkId;
                        if (!id) return;
                        const data = await penpaperBulkSegmentsMutation.mutateAsync({ classworkId: Number(id), runId, segments });
                        return data;
                      } : undefined}
                      onBulkImportFinalize={wizardReturnMode === 'paper_presence' ? async ({ runId }) => {
                        const id = wizardClassworkId;
                        if (!id) return;
                        await penpaperBulkFinalizeMutation.mutateAsync({ classworkId: Number(id), runId });
                      } : undefined}
                    />
                  ) : (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                      {wizardReturnMode === 'paper_photo_upload'
                        ? 'Bei Abfotografieren laden Schüler ihre Seiten selbst hoch. Auf Mobilgeräten öffnet der Upload direkt die Kamera. Lehrer-Import ist hier optional und nicht Teil des Standardflows.'
                        : 'Bei digitalem Modus erfolgen Rückläufe direkt über die Schülerabgabe im System.'}
                    </div>
                  )}
                  <div className={WIZARD_ACTION_ROW}>
                    <button type="button" className={WIZARD_BTN_BACK} onClick={() => setWizardStep(3)}>
                      Zurück
                    </button>
                    <button
                      type="button"
                      className={WIZARD_BTN_PRIMARY}
                      onClick={async () => {
                        const saved = await patchWizard({
                          estimated_duration_minutes: wEstimatedMinutes.trim() ? Math.min(600, Math.max(1, Number(wEstimatedMinutes))) : null,
                          starts_at: localDatetimeToIso(wStartsAt),
                          due_at: localDatetimeToIso(wDueAt),
                          latest_submit_at: localDatetimeToIso(wLatestSubmitAt),
                          max_attempts: Math.min(20, Math.max(1, parseInt(wMaxAttempts, 10) || 1)),
                          seb_mode: wSebMode,
                          student_upload_mode: wStudentUploadMode
                        });
                        if (!saved) return;
                        setWizardStep(5);
                      }}
                    >
                      Weiter
                    </button>
                    <button type="button" className={WIZARD_BTN_DRAFT} onClick={() => void saveDraft()}>
                      Entwurf speichern
                    </button>
                  </div>
                </div>
              )}

              {wizardStep === 3 && wizardClassworkId && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-gray-900">Korrekturraster & Kompetenzen</h2>
                  <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-4 space-y-2">
                    <label className="text-sm font-semibold text-gray-900">Notenschema (verbindlich)</label>
                    <p className="text-xs text-gray-600">
                      Wird für die automatische Notenbildung nach der LLM-Korrektur verwendet (Prozent → Note). Ohne Schema keine automatische Note.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        className="input-field max-w-md"
                        value={wGradeSchemaId}
                        onChange={(e) => setWGradeSchemaId(e.target.value)}
                        disabled={wizardGradeSchemasQuery.isLoading}
                      >
                        <option value="">— Bitte Notenschema wählen —</option>
                        {(wizardGradeSchemasQuery.data || []).map((s) => (
                          <option key={s.id} value={String(s.id)}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn-secondary text-sm"
                        disabled={!wSubjectId}
                        onClick={async () => {
                          const sid = Number(wSubjectId);
                          if (!sid) {
                            toast.error('Zuerst ein Fach in Schritt 1 wählen.');
                            return;
                          }
                          try {
                            const d = await fetchDefaultGradeSchemaForSubject(sid);
                            if (d?.grade_schema_id) {
                              setWGradeSchemaId(String(d.grade_schema_id));
                              toast.success('Fach-Standard übernommen');
                            } else {
                              toast.error('Kein Standard-Notenschema für dieses Fach hinterlegt (Einstellungen).');
                            }
                          } catch {
                            toast.error('Standard konnte nicht geladen werden');
                          }
                        }}
                      >
                        Fach-Standard übernehmen
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1">Korrekturraster</label>
                    <textarea
                      className="input-field min-h-[100px]"
                      value={wExpectation}
                      onChange={(e) => setWExpectation(e.target.value)}
                      placeholder="Kompetenzen / Anforderungen …"
                    />
                    <p className="text-xs text-gray-500 mt-1.5 mb-2">
                      Zusätzlich Dateien hochladen (z. B. PDF/Word) — nur für die Lehrkraft sichtbar, erscheinen nicht bei Schülern.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        id="classwork-expectation-file-input"
                        type="file"
                        className="hidden"
                        disabled={uploadTeacherContextFileMutation.isLoading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f && wizardClassworkId) {
                            void uploadTeacherContextFileMutation.mutateAsync({
                              file: f,
                              materialType: 'erwartungshorizont',
                              classworkId: Number(wizardClassworkId)
                            });
                          }
                          e.currentTarget.value = '';
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline font-medium border-gray-300 inline-flex items-center gap-1.5"
                        disabled={uploadTeacherContextFileMutation.isLoading}
                        onClick={() => document.getElementById('classwork-expectation-file-input')?.click()}
                      >
                        <Upload className="h-3.5 w-3.5" aria-hidden />
                        Datei hochladen
                      </button>
                    </div>
                    <div className="mt-3 rounded-lg border border-gray-200 bg-slate-50/90 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <span className="text-sm font-semibold text-gray-900">Hochgeladene Dateien</span>
                        <span className="text-xs font-medium text-gray-600 tabular-nums">
                          {expectationHorizonMaterials.length} Datei(en)
                        </span>
                      </div>
                      {expectationHorizonMaterials.length === 0 ? (
                        <p className="text-sm text-gray-600 rounded-md border border-dashed border-amber-200 bg-amber-50/80 px-3 py-2.5">
                          <span className="font-medium text-amber-900">Noch keine Datei</span> zum Korrekturraster hochgeladen.
                        </p>
                      ) : (
                        <ul className="space-y-2 text-sm">
                          {expectationHorizonMaterials.map((m: any) => (
                            <li
                              key={m.id}
                              className="flex flex-wrap justify-between gap-2 items-center border border-gray-200 rounded-lg px-3 py-2 bg-white shadow-sm"
                            >
                              <div className="min-w-0 flex items-start gap-2 flex-1">
                                <FileText className="h-4 w-4 text-primary-600 flex-shrink-0 mt-0.5" aria-hidden />
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-900 truncate">{m.title || m.original_name || 'Datei'}</p>
                                  {m.mime_type ? (
                                    <p className="text-xs text-gray-500 truncate">{String(m.mime_type)}</p>
                                  ) : null}
                                </div>
                              </div>
                              <span className="flex flex-wrap gap-2 shrink-0">
                                <a
                                  className="text-primary-700 text-sm font-medium hover:underline"
                                  href={`/api/classwork/${wizardClassworkId}/materials/${m.id}/download`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Download
                                </a>
                                <button
                                  type="button"
                                  className="text-red-600 text-sm font-medium hover:underline"
                                  onClick={() =>
                                    wizardClassworkId &&
                                    deleteMaterialMutation.mutate({
                                      classworkId: Number(wizardClassworkId),
                                      materialId: Number(m.id)
                                    })
                                  }
                                >
                                  Entfernen
                                </button>
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1">Musterlösung (nur Lehrkraft)</label>
                    <textarea
                      className="input-field min-h-[100px]"
                      value={wSample}
                      onChange={(e) => setWSample(e.target.value)}
                      placeholder="Optional für LLM-Kontext …"
                    />
                    <p className="text-xs text-gray-500 mt-1.5 mb-2">
                      Zusätzlich Dateien hochladen — nur für die Lehrkraft sichtbar, nicht für Schüler.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        id="classwork-sample-file-input"
                        type="file"
                        className="hidden"
                        disabled={uploadTeacherContextFileMutation.isLoading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f && wizardClassworkId) {
                            void uploadTeacherContextFileMutation.mutateAsync({
                              file: f,
                              materialType: 'musterloesung',
                              classworkId: Number(wizardClassworkId)
                            });
                          }
                          e.currentTarget.value = '';
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline font-medium border-gray-300 inline-flex items-center gap-1.5"
                        disabled={uploadTeacherContextFileMutation.isLoading}
                        onClick={() => document.getElementById('classwork-sample-file-input')?.click()}
                      >
                        <Upload className="h-3.5 w-3.5" aria-hidden />
                        Datei hochladen
                      </button>
                    </div>
                    <div className="mt-3 rounded-lg border border-gray-200 bg-slate-50/90 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <span className="text-sm font-semibold text-gray-900">Hochgeladene Dateien</span>
                        <span className="text-xs font-medium text-gray-600 tabular-nums">
                          {sampleSolutionMaterials.length} Datei(en)
                        </span>
                      </div>
                      {sampleSolutionMaterials.length === 0 ? (
                        <p className="text-sm text-gray-600 rounded-md border border-dashed border-amber-200 bg-amber-50/80 px-3 py-2.5">
                          <span className="font-medium text-amber-900">Noch keine Datei</span> zur Musterlösung hochgeladen.
                        </p>
                      ) : (
                        <ul className="space-y-2 text-sm">
                          {sampleSolutionMaterials.map((m: any) => (
                            <li
                              key={m.id}
                              className="flex flex-wrap justify-between gap-2 items-center border border-gray-200 rounded-lg px-3 py-2 bg-white shadow-sm"
                            >
                              <div className="min-w-0 flex items-start gap-2 flex-1">
                                <FileText className="h-4 w-4 text-primary-600 flex-shrink-0 mt-0.5" aria-hidden />
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-900 truncate">{m.title || m.original_name || 'Datei'}</p>
                                  {m.mime_type ? (
                                    <p className="text-xs text-gray-500 truncate">{String(m.mime_type)}</p>
                                  ) : null}
                                </div>
                              </div>
                              <span className="flex flex-wrap gap-2 shrink-0">
                                <a
                                  className="text-primary-700 text-sm font-medium hover:underline"
                                  href={`/api/classwork/${wizardClassworkId}/materials/${m.id}/download`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Download
                                </a>
                                <button
                                  type="button"
                                  className="text-red-600 text-sm font-medium hover:underline"
                                  onClick={() =>
                                    wizardClassworkId &&
                                    deleteMaterialMutation.mutate({
                                      classworkId: Number(wizardClassworkId),
                                      materialId: Number(m.id)
                                    })
                                  }
                                >
                                  Entfernen
                                </button>
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-md btn-primary shadow-sm font-semibold whitespace-normal text-left sm:text-center sm:whitespace-nowrap"
                      onClick={async () => {
                        const saved = await patchWizard({
                          expectation_horizon: wExpectation.trim() || null,
                          sample_solution: wSample.trim() || null,
                          grade_schema_id: wGradeSchemaId ? Number(wGradeSchemaId) : null
                        });
                        if (!saved) return;
                        suggestMutation.mutate();
                      }}
                      disabled={suggestMutation.isLoading}
                    >
                      Kompetenzen per LLM vorschlagen (Material + Curriculum)
                    </button>
                  </div>
                  <ClassworkCompetencySelector
                    items={suggestions}
                    onConfirm={(payload) => confirmMutation.mutate(payload)}
                    loading={confirmMutation.isLoading}
                  />
                  <div className={WIZARD_ACTION_ROW}>
                    <button type="button" className={WIZARD_BTN_BACK} onClick={() => setWizardStep(2)}>
                      Zurück
                    </button>
                    <button
                      type="button"
                      className={WIZARD_BTN_PRIMARY}
                      onClick={async () => {
                        if (!wGradeSchemaId || !Number(wGradeSchemaId)) {
                          toast.error('Bitte ein Notenschema wählen (Pflichtfeld).');
                          return;
                        }
                        const saved = await patchWizard({
                          expectation_horizon: wExpectation.trim() || null,
                          sample_solution: wSample.trim() || null,
                          grade_schema_id: Number(wGradeSchemaId)
                        });
                        if (!saved) return;
                        setWizardStep(4);
                      }}
                    >
                      Weiter
                    </button>
                    <button type="button" className={WIZARD_BTN_DRAFT} onClick={() => void saveDraft()}>
                      Entwurf speichern
                    </button>
                  </div>
                </div>
              )}

              {wizardStep === 5 && wizardClassworkId && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold text-gray-900">Teilnehmer & Veröffentlichung</h2>
                  <div className="rounded-lg border border-gray-200 p-4 bg-white space-y-3">
                    <p className="text-sm font-semibold text-gray-900">Teilnehmerzuweisung</p>
                    <div className="flex flex-wrap gap-2">
                      {(['class', 'course', 'group', 'manual'] as const).map((scope) => (
                        <button
                          key={scope}
                          type="button"
                          className={`px-3 py-1 text-sm rounded-lg border-2 ${
                            assignmentScope === scope
                              ? 'border-primary-600 bg-primary-50'
                              : 'border-gray-200 hover:border-primary-200'
                          }`}
                          onClick={() => setAssignmentScope(scope)}
                        >
                          {scope === 'class' ? 'Klasse' : scope === 'course' ? 'Kurs' : scope === 'group' ? 'Gruppe' : 'Einzelne'}
                        </button>
                      ))}
                    </div>
                    {assignmentScope === 'class' ? (
                      <select className="input-field max-w-md" value={assignmentClassName} onChange={(e) => setAssignmentClassName(e.target.value)}>
                        <option value="">Klasse wählen</option>
                        {classOptions.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    {assignmentScope === 'course' ? (
                      <select className="input-field max-w-md" value={assignmentCourseId} onChange={(e) => setAssignmentCourseId(e.target.value)}>
                        <option value="">Kurs wählen</option>
                        {(teachingMetaQuery.data?.courses || []).map((c) => (
                          <option key={c.id} value={String(c.id)}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    {assignmentScope === 'group' ? (
                      <select className="input-field max-w-md" value={assignmentGroupId} onChange={(e) => setAssignmentGroupId(e.target.value)}>
                        <option value="">Gruppe wählen</option>
                        {(teachingMetaQuery.data?.groups || []).map((g) => (
                          <option key={g.id} value={String(g.id)}>
                            {g.name}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    {assignmentScope === 'manual' ? (
                      <div>
                        <input
                          className="input-field max-w-md mb-2"
                          value={manualQuery}
                          onChange={(e) => setManualQuery(e.target.value)}
                          placeholder="Schüler suchen …"
                        />
                        <div className="max-h-40 overflow-auto border rounded-lg">
                          {(filteredManualStudents || []).map((s) => {
                            const unid = String(s.sophomorixUnid || '').trim();
                            if (!unid) return null;
                            return (
                              <label key={unid} className="flex items-center gap-2 px-3 py-2 border-b text-sm">
                                <input
                                  type="checkbox"
                                  checked={!!manualSelectedById[unid]}
                                  onChange={(e) => setManualSelectedById((prev) => ({ ...prev, [unid]: e.target.checked }))}
                                />
                                {s.Vorname} {s.Name} · {unid}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                    {linkedTeachingUnitId ? (
                      <div className="pt-2 border-t border-gray-100">
                        <p className="text-xs text-gray-600 mb-2">Optional: alle Teilnehmer der verknüpften Unterrichtseinheit zuweisen.</p>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary font-medium"
                          disabled={assignFromUnitMutation.isLoading}
                          onClick={() => assignFromUnitMutation.mutate()}
                        >
                          Alle aus Unterrichtseinheit zuweisen
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <div className="border-t border-gray-200 pt-3">
                    <button
                      type="button"
                      className="text-xs font-medium text-gray-700 underline"
                      onClick={() => setShowWizardAdvancedSchedule((prev) => !prev)}
                    >
                      {showWizardAdvancedSchedule ? 'Erweiterte Durchführung ausblenden' : 'Erweiterte Durchführung anzeigen'}
                    </button>
                    {showWizardAdvancedSchedule ? (
                      <div className="mt-3 space-y-3">
                        <div>
                          <label className="text-xs font-medium text-gray-700 mb-1">Geschätzte Bearbeitungszeit (Min)</label>
                          <input
                            className="input-field"
                            type="number"
                            min={1}
                            max={600}
                            value={wEstimatedMinutes}
                            onChange={(e) => setWEstimatedMinutes(e.target.value)}
                          />
                        </div>
                        <div className="grid sm:grid-cols-1 gap-3">
                          <div>
                            <label className="text-xs font-medium text-gray-700 mb-1">Start (optional)</label>
                            <input
                              className="input-field"
                              type="datetime-local"
                              value={wStartsAt}
                              onChange={(e) => setWStartsAt(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-700 mb-1">Abgabe bis (optional)</label>
                            <input className="input-field" type="datetime-local" value={wDueAt} onChange={(e) => setWDueAt(e.target.value)} />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-700 mb-1">Späteste Abgabe (optional)</label>
                            <input
                              className="input-field"
                              type="datetime-local"
                              value={wLatestSubmitAt}
                              onChange={(e) => setWLatestSubmitAt(e.target.value)}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-700 mb-1">Max. Versuche</label>
                          <input
                            className="input-field max-w-xs"
                            type="number"
                            min={1}
                            max={20}
                            value={wMaxAttempts}
                            onChange={(e) => setWMaxAttempts(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-700 mb-1">Prüfungsmodus (SEB)</label>
                          <select className="input-field max-w-md" value={wSebMode} onChange={(e) => setWSebMode(e.target.value as any)}>
                            <option value="seb_optional">SEB optional</option>
                            <option value="seb_required">SEB erforderlich</option>
                          </select>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Zusammenfassung</h3>
                  <dl className="text-sm space-y-2 border rounded-lg p-4 bg-gray-50">
                    <div>
                      <dt className="text-gray-500">Titel</dt>
                      <dd className="font-medium">{wizardDetailQuery.data?.item?.title}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Status</dt>
                      <dd>{wizardDetailQuery.data?.item?.status}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Rücklaufmodus</dt>
                      <dd className="font-medium text-gray-900">{returnModeLabel(wizardReturnMode)}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Notenschema (KI / automatische Note)</dt>
                      <dd className="font-medium text-gray-900">
                        {wizardBoundSchemaName ? (
                          wizardBoundSchemaName
                        ) : (
                          <span className="text-rose-700">Nicht gewählt – in Schritt 3 erforderlich</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Aufgaben-Materialien (Schüler:innen)</dt>
                      <dd>
                        {
                          wizardMaterials.filter(
                            (m: any) => !isExpectationHorizonMaterial(m) && !isMusterloesungMaterial(m)
                          ).length
                        }{' '}
                        Datei(en)/Link(s)
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Korrekturraster (Text)</dt>
                      <dd className="text-gray-800 whitespace-pre-wrap break-words">
                        {wExpectation.trim() ? (
                          <>
                            <span className="font-medium text-emerald-800">Erfasst ({wExpectation.trim().length} Zeichen)</span>
                            <span className="block mt-1 text-gray-600 max-h-24 overflow-y-auto text-xs border rounded bg-white p-2">
                              {wExpectation.trim().slice(0, 500)}
                              {wExpectation.trim().length > 500 ? '…' : ''}
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-500 italic">Kein Text</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Korrekturraster (Dateien, nur Lehrkraft)</dt>
                      <dd>
                        {expectationHorizonMaterials.length === 0 ? (
                          <span className="text-gray-500 italic">Keine Datei hochgeladen</span>
                        ) : (
                          <ul className="mt-1 space-y-1">
                            {expectationHorizonMaterials.map((m: any) => (
                              <li key={m.id} className="flex flex-wrap items-center gap-2 text-gray-900">
                                <FileText className="h-4 w-4 text-primary-600 flex-shrink-0" aria-hidden />
                                <span className="font-medium truncate">{m.title || m.original_name}</span>
                                {m.mime_type ? (
                                  <span className="text-xs text-gray-500">({String(m.mime_type)})</span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Musterlösung (Text)</dt>
                      <dd className="text-gray-800 whitespace-pre-wrap break-words">
                        {wSample.trim() ? (
                          <>
                            <span className="font-medium text-emerald-800">Erfasst ({wSample.trim().length} Zeichen)</span>
                            <span className="block mt-1 text-gray-600 max-h-24 overflow-y-auto text-xs border rounded bg-white p-2">
                              {wSample.trim().slice(0, 500)}
                              {wSample.trim().length > 500 ? '…' : ''}
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-500 italic">Kein Text</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Musterlösung (Dateien, nur Lehrkraft)</dt>
                      <dd>
                        {sampleSolutionMaterials.length === 0 ? (
                          <span className="text-gray-500 italic">Keine Datei hochgeladen</span>
                        ) : (
                          <ul className="mt-1 space-y-1">
                            {sampleSolutionMaterials.map((m: any) => (
                              <li key={m.id} className="flex flex-wrap items-center gap-2 text-gray-900">
                                <FileText className="h-4 w-4 text-primary-600 flex-shrink-0" aria-hidden />
                                <span className="font-medium truncate">{m.title || m.original_name}</span>
                                {m.mime_type ? (
                                  <span className="text-xs text-gray-500">({String(m.mime_type)})</span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        )}
                      </dd>
                    </div>
                  </dl>
                  <p className="text-sm text-gray-600">
                    Mit „Klassenarbeit erstellen (aktivieren)“ wird die Klassenarbeit für zugewiesene Schüler sichtbar aktiviert.
                  </p>
                  <div className={WIZARD_ACTION_ROW}>
                    <button type="button" className={WIZARD_BTN_BACK} onClick={() => setWizardStep(4)}>
                      Zurück
                    </button>
                    <button type="button" className={WIZARD_BTN_PRIMARY_LG} onClick={() => void handleFinalize()}>
                      Klassenarbeit erstellen (aktivieren)
                    </button>
                  </div>
                </div>
              )}

              {wizardStep === 5 && !wizardClassworkId && (
                <p className="text-sm text-amber-800">Bitte die vorherigen Schritte abschließen.</p>
              )}
            </div>
          </div>
        )}

        {listDetailId && pipelineReviewAssignmentId ? (
          <ClassworkPipelineReviewModal
            classworkId={Number(listDetailId)}
            assignmentId={pipelineReviewAssignmentId}
            open={Boolean(listDetailId && pipelineReviewAssignmentId)}
            onClose={() => setPipelineReviewAssignmentId(null)}
          />
        ) : null}

        <OnlyOfficeSessionModal
          isOpen={!!materialSession}
          assignmentId={materialEditorTargetId}
          session={materialSession}
          title="Material in OnlyOffice bearbeiten"
          showFinalize={false}
          onClose={() => {
            setMaterialSession(null);
            setMaterialEditorTargetId(null);
            if (activeCwForModal) void queryClient.invalidateQueries(['classwork-detail', activeCwForModal]);
          }}
        />
      </div>
    </div>
  );
}
